import { useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback, memo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { putFile, getFile, deleteFile, clearAllFiles } from './fileStore';
import { isEntryStale, isMethodStale, staleReason, staleNotes, misreadValues, revisionSource, findRevisionOf } from './calcVersions';
import { markdownToPlainText } from './markdownToPlainText';
import { TERMS_VERSION, TERMS_URL, PRIVACY_URL, AI_TERMS_URL } from './legal';
import { isProseOutput } from './calcOutputLayout';
import { wheelUnpins, touchUnpins, keyUnpins, wheelHeadsDown, touchHeadsDown, keyHeadsDown, shouldRepin, enterSends } from './scrollStick';
import { stableSplit } from './streamMarkdown';
import { keyboardInset, showJumpToLatest, preservedScrollTop } from './keyboardInset';
import { ANSWER_LANGUAGES, languageInfo, loadAnswerLanguage, saveAnswerLanguage, rememberLanguageAccount, loadInitialAnswerLanguage } from './answerLanguages';
import { I18nProvider, makeT, makeTc, makeNum, resolveLanguages, useT, useI18n, tNodes } from './i18n';
import { starterStringsFor, starterLangAttr } from './starterStrings';
import './App.css';
import CalculatorDashboard from './components/AquaIQ_Calculator_Dashboard';
// Site brand logo (fish mark + wordmark) for the signed-out landing —
// black-on-transparent for light mode, white-on-transparent for dark.
import logoBlack from './assets/aquanetica-logo-black.png';
import logoWhite from './assets/aquanetica-logo-white.png';
// Icon-only fish mark (from the site's own 512px icon; white derived by tint,
// alpha preserved) for the branded loading indicator.
import iconBlack from './assets/aquanetica-icon-black.png';
import iconWhite from './assets/aquanetica-icon-white.png';

/*
 * THE ONE FOLLOW. Every "take me to the newest text" in the chat goes through
 * here: the stream, a new turn, the jump pill, the keyboard opening, the
 * composer growing, a rotation.
 *
 * It scrolls the document to its maximum, NOT endRef.scrollIntoView. The
 * conversation reserves bottom padding equal to the measured composer (plus
 * the keyboard), so the maximum IS "last line just above the composer".
 * scrollIntoView({ block: 'end' }) aligned the end marker with the bottom of
 * the window instead, which the fixed composer covers; that is why the newest
 * line streamed in sliced by the composer's top edge on every frame.
 */
function scrollToLatest(behavior = 'auto') {
  const root = document.scrollingElement || document.documentElement;
  window.scrollTo({ top: root.scrollHeight, behavior });
}

/*
 * What can be attached. Images and PDFs go to the model as they are; Word,
 * Excel and PowerPoint files have their text pulled out on the server.
 * The extension is the fallback because some systems (Windows without Office
 * installed, some Android file pickers) hand over these files with an empty
 * or generic type.
 */
const UPLOAD_TYPES = {
  'image/jpeg': ['jpg', 'jpeg'],
  'image/png': ['png'],
  'image/gif': ['gif'],
  'image/webp': ['webp'],
  'application/pdf': ['pdf'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['docx'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['xlsx'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['pptx'],
};
/*
 * Text and code files, which the server decodes and sends as text. Here the
 * extension decides, not the reported type: the same .csv arrives as
 * text/csv on a Mac, application/vnd.ms-excel on Windows with Excel, and
 * with no type at all elsewhere, and .r and .py rarely have a type. The
 * server checks the bytes really are text.
 */
const TEXT_UPLOAD_TYPES = {
  txt: 'text/plain',
  md: 'text/markdown',
  csv: 'text/csv',
  json: 'application/json',
  r: 'text/x-r',
  py: 'text/x-python',
};
const GENERIC_TYPES = new Set(['', 'application/octet-stream', 'application/zip']);
const UPLOAD_ACCEPT = [
  ...Object.entries(UPLOAD_TYPES).flatMap(([type, exts]) => [type, ...exts.map((e) => '.' + e)]),
  ...Object.keys(TEXT_UPLOAD_TYPES).map((e) => '.' + e),
  'text/plain', 'text/markdown', 'text/csv', 'application/json',
].join(',');
export function uploadMediaType(file) {
  if (!file) return null;
  const name = String(file.name || '');
  const ext = name.includes('.') ? name.toLowerCase().split('.').pop() : '';
  if (TEXT_UPLOAD_TYPES[ext]) return TEXT_UPLOAD_TYPES[ext];
  if (UPLOAD_TYPES[file.type]) return file.type;
  const byExt = Object.keys(UPLOAD_TYPES).find((type) => UPLOAD_TYPES[type].includes(ext));
  if (byExt) return GENERIC_TYPES.has(file.type) ? byExt : null;
  // A plain text file with no extension at all (a README, a pasted note).
  return !ext && file.type === 'text/plain' ? 'text/plain' : null;
}
// The server's file refusals, by code, to the member's own language.
const FILE_ERROR_KEYS = {
  file_unsupported: 'file.badType',
  file_unreadable: 'file.unreadable',
  file_no_text: 'file.noText',
  file_too_big: 'file.tooBig',
  file_not_text: 'file.notText',
};

/** Distance from the bottom of the document, in px. */
function distanceFromBottom() {
  const root = document.scrollingElement || document.documentElement;
  return root.scrollHeight - window.innerHeight - window.scrollY;
}

/**
 * A touch device for Enter purposes: no hover and a coarse pointer. A laptop
 * with a touchscreen keeps its hardware Enter-sends; an iPad with a trackpad
 * reports a fine pointer and does too.
 */
function isTouchComposer() {
  try { return !!window.matchMedia && window.matchMedia('(hover: none) and (pointer: coarse)').matches; }
  catch { return false; }
}

// Build a trimmed multi-turn history to send to /api/chat so Gia keeps
// context across turns. Bounded to keep token cost sane: last `maxTurns`
// completed Q&A pairs, each answer capped, whole history capped, oldest
// dropped first. Returns alternating user/assistant messages.
function buildHistoryMessages(messages, { maxTurns = 6, perAnswerChars = 1500, totalChars = 12000 } = {}) {
  const pairs = (messages || []).filter(m => m && m.question && m.answer && m.answer.trim());
  const recent = pairs.slice(-maxTurns).map(m => ({
    q: String(m.question).replace(/\s*\[attached:[^\]]*\]/gi, '').trim().slice(0, 2000),
    a: String(m.answer).replace(/\[PRODUCTS:[^\]]*\]/gi, '').replace(/\s*\[\d+\]/g, '').trim().slice(0, perAnswerChars),
  })).filter(m => m.q && m.a);

  // Enforce the total-character budget, dropping the oldest turns first.
  let budget = totalChars;
  const kept = [];
  for (let i = recent.length - 1; i >= 0; i--) {
    const size = recent[i].q.length + recent[i].a.length;
    if (budget - size < 0 && kept.length) break;
    budget -= size;
    kept.unshift(recent[i]);
  }

  const out = [];
  for (const m of kept) {
    out.push({ role: 'user', content: m.q });
    out.push({ role: 'assistant', content: m.a });
  }
  return out;
}

function buildProjectContext(project) {
  if (!project) return '';
  const lines = [];
  const p = project.systemProfile;
  if (p) {
    if (p.species) lines.push(`Species: ${p.species}`);
    if (p.systemType) lines.push(`System type: ${p.systemType}`);
    if (p.volume) lines.push(`Total volume: ${p.volume} L`);
    if (p.targetTemp) lines.push(`Target temperature: ${p.targetTemp}°C`);
    if (p.lifeStage) lines.push(`Life stage: ${p.lifeStage}`);
    if (p.location) lines.push(`Location: ${p.location}`);
    if (p.notes) lines.push(`Additional context: ${p.notes}`);
  }
  let calcLines = [];
  try {
    const all = JSON.parse(localStorage.getItem('aqua-iq-calc-results') || '{}');
    const saved = Array.isArray(all[project.id]) ? all[project.id] : [];
    calcLines = saved.map((r) => {
      // A result computed by a superseded method must never reach Gia as a
      // citable figure — the surrounding prompt tells it to treat these as
      // ground truth. Withhold the numbers and say why, so Gia can tell the
      // user the result is stale instead of quoting a wrong one or claiming
      // no calculation exists.
      if (isEntryStale(r)) {
        return `- ${r.calcLabel} (${r.ts}): SUPERSEDED, figures withheld. ${staleReason(r)} Tell the user to re-run this calculation in the calculator; do not estimate or reconstruct the numbers yourself.`;
      }
      const outs = (r.outputs || []).map((o) => `${o.label}: ${o.value}${o.unit ? ' ' + o.unit : ''}`).join(', ');
      /*
       * Superseded by a revision is a weaker claim than superseded by method:
       * the figures are still correct for the inputs they were given, so unlike
       * the stale case they are kept. Gia is just told a newer one exists so it
       * quotes that instead of an abandoned draft.
       */
      const newer = findRevisionOf(r, saved);
      if (newer) {
        return `- ${r.calcLabel} (${r.ts}): ${outs} [SUPERSEDED by a revision the user saved at ${newer.ts}; quote the newer one unless asked about the earlier version]`;
      }
      if (r.supersedes) {
        return `- ${r.calcLabel} (${r.ts}): ${outs} [this is the user's revision of an earlier ${r.supersedes.calcLabel || 'result'} from ${r.supersedes.ts}]`;
      }
      return `- ${r.calcLabel} (${r.ts}): ${outs}`;
    });
  } catch (e) { calcLines = []; }
  if (lines.length === 0 && calcLines.length === 0) return '';
  let out = `\n\nIMPORTANT: The following is the user's own project data. Treat it as authoritative ground truth. When the user asks about "my calculations", "my results", "my numbers", whether their calcs are right, or anything similar, answer directly and specifically from the data below. Do not ask the user to supply figures that are already given here.`;
  if (lines.length) out += `\n\nSystem profile for this project:\n${lines.join('\n')}`;
  if (calcLines.length) out += `\n\nThe user's saved calculator results for this project (real results the user computed and saved; cite these exact figures when relevant):\n${calcLines.join('\n')}`;
  return out;
}

/*
 * Branded three-state loading indicator, driven by what the backend is doing:
 * the fish mark SWIMS while /api/search runs against Qdrant, DARTS once when
 * retrieval returns (before the first token), and BOBS (vertical only — no
 * sideways travel next to streaming text) while the answer streams. CSS swaps
 * the black/white mark per theme; prefers-reduced-motion disables all three.
 */
function FishLoader({ phase, size = 24, inline = false }) {
  return (
    <span className={'fish-loader fish-' + phase + (inline ? ' fish-inline' : '')} aria-hidden="true">
      <img className="fish-img fish-img-light" src={iconBlack} alt="" style={{ height: size }} />
      <img className="fish-img fish-img-dark" src={iconWhite} alt="" style={{ height: size }} />
    </span>
  );
}

/*
 * One translated line beside English legal text: the terms are provided in
 * English and English governs. Rendered only when the interface is not
 * English, so the English interface is unchanged.
 */
function EnglishGoverningNote({ className = 'legal-governing' }) {
  const { t, uiLang } = useI18n();
  if (uiLang === 'en') return null;
  return <p className={className} lang={uiLang}>{t('legal.englishGoverning')}</p>;
}

/* The site's Privacy Policy and Terms of Service, on screens without the notice line under the chat box. */
function LegalLinks() {
  return (
    <>
      {' · '}
      <a className="terms-link" href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">Privacy</a>
      {' · '}
      <a className="terms-link" href={TERMS_URL} target="_blank" rel="noopener noreferrer">Terms</a>
    </>
  );
}

/*
 * The operator's company details, on every IQ screen. English everywhere, like the terms.
 * Chat screens carry Privacy and Terms in the notice under the box, so the links
 * are added here only where that notice isn't shown (sign-in screen, Calculator).
 */
function CompanyFooter({ links = false, className = 'company-footer' }) {
  return (
    <p className={className} lang="en">
      © 2026 AquaNetica Group Ltd · Company no. 17511062 · Registered in England and Wales
      {links && <LegalLinks />}
    </p>
  );
}

// Shown instead of the app when auth is enabled and the user is not signed in.
// Styled to match aquanetica.com (site logo, light blue-grey page, pill teal
// button) rather than a generic auth screen; CSS swaps the logo per theme.
function LandingScreen() {
  const t = useT();
  return (
    <div className="landing">
      <div className="landing-card">
        <img className="landing-logo landing-logo-light" src={logoBlack} alt="AquaNetica IQ" />
        <img className="landing-logo landing-logo-dark" src={logoWhite} alt="AquaNetica IQ" />
        <p className="landing-lead">{t('landing.lead')}</p>
        <a className="landing-btn" href={LOGIN_URL}>{t('landing.signIn')}</a>
        {/*
          Same destination as the Sign in button: the /iq-login bridge sends a
          logged-out visitor to the branded signup page, so one URL serves both.
          Kept visually quiet so the button stays the primary CTA.
        */}
        <p className="landing-note">
          <a className="landing-note-link" href={LOGIN_URL}>{t('landing.signUpFree')}</a>
        </p>
      </div>
      <CompanyFooter links />
    </div>
  );
}

/*
 * Markdown for a streaming answer, split so completed content holds still.
 *
 * The stable head only changes when a whole block completes, so React.memo
 * makes finished paragraphs, lists and headings parse once and then sit
 * untouched while the tail streams; nothing that has already rendered can
 * reflow or flicker because of what arrives after it. The tail re-parses per
 * frame, which is where an in-progress **bold or list item legitimately
 * completes in place. Once the stream ends the caller goes back to rendering
 * the full text in one piece, exactly as before, so the finished answer is
 * byte-identical to the non-streaming path.
 */
const StableMarkdown = memo(function StableMarkdown({ text }) {
  return <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>;
});

function StreamingMarkdown({ text }) {
  const { stable, tail } = stableSplit(text);
  return (
    <>
      {stable && <StableMarkdown text={stable} />}
      {tail && <ReactMarkdown remarkPlugins={[remarkGfm]}>{tail}</ReactMarkdown>}
    </>
  );
}

function ProjectProfileModal({ project, onSave, onClose }) {
  const [profile, setProfile] = useState(
    project.systemProfile || {
      species: '', systemType: '', volume: '', targetTemp: '',
      lifeStage: '', location: '', notes: ''
    }
  );
  const update = (field, value) => setProfile(p => ({ ...p, [field]: value }));
  const t = useT();

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('profile.title', { name: project.name })}</h2>
          <button className="icon-btn" onClick={onClose} aria-label={t('common.close')}>×</button>
        </div>
        <div className="modal-subtitle">
          {t('profile.subtitle')}
        </div>
        <div className="modal-body">
          <div className="field">
            <label>{t('profile.species')}</label>
            <input value={profile.species} onChange={e => update('species', e.target.value)} placeholder={t('profile.speciesPlaceholder')} />
          </div>
          <div className="field">
            <label>{t('profile.systemType')}</label>
            {/* Values stay English: they are saved on the project and sent to Gia. */}
            <select value={profile.systemType} onChange={e => update('systemType', e.target.value)}>
              <option value="">{t('profile.select')}</option>
              <option value="RAS">{t('profile.sys.ras')}</option>
              <option value="Flow-through">{t('profile.sys.flow')}</option>
              <option value="Pond">{t('profile.sys.pond')}</option>
              <option value="Cage / sea pen">{t('profile.sys.cage')}</option>
              <option value="Hatchery">{t('profile.sys.hatchery')}</option>
              <option value="Other">{t('profile.sys.other')}</option>
            </select>
          </div>
          <div className="field-row">
            <div className="field">
              <label>{t('profile.volume')}</label>
              <input value={profile.volume} onChange={e => update('volume', e.target.value)} placeholder="50000" />
            </div>
            <div className="field">
              <label>{t('profile.temp')}</label>
              <input value={profile.targetTemp} onChange={e => update('targetTemp', e.target.value)} placeholder="12" />
            </div>
          </div>
          <div className="field-row">
            <div className="field">
              <label>{t('profile.lifeStage')}</label>
              <input value={profile.lifeStage} onChange={e => update('lifeStage', e.target.value)} placeholder={t('profile.lifeStagePlaceholder')} />
            </div>
            <div className="field">
              <label>{t('profile.location')}</label>
              <input value={profile.location} onChange={e => update('location', e.target.value)} placeholder={t('profile.locationPlaceholder')} />
            </div>
          </div>
          <div className="field">
            <label>{t('profile.notes')}</label>
            <textarea value={profile.notes} onChange={e => update('notes', e.target.value)} placeholder={t('profile.notesPlaceholder')} rows={3} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
          <button className="btn-primary" onClick={() => { onSave(profile); onClose(); }}>{t('profile.save')}</button>
        </div>
      </div>
    </div>
  );
}

function HelpModal({ onClose }) {
  const t = useT();
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('help.title')}</h2>
          <button className="icon-btn" onClick={onClose} aria-label={t('common.close')}>×</button>
        </div>
        <div className="modal-body">
          <p className="legal-text">{t('help.about')}</p>
          <EnglishGoverningNote />
          {/* Disclaimer: English in every interface language. */}
          <p className="legal-text" lang="en">
            AquaNetica IQ is provided for informational purposes only. It does not provide
            professional engineering, veterinary, legal, or financial advice, and no such
            professional relationship is created by using it. Outputs may be incomplete or
            inaccurate and are given without any warranty of accuracy. You are responsible
            for any decisions made using the platform, and should independently verify
            outputs with a suitably qualified professional before acting on them.
          </p>
        </div>
      </div>
    </div>
  );
}

// Terms & disclaimer. The wording below has been legally reviewed — change it
// only alongside a review, not as incidental copy editing.
// SOLICITOR TO CONFIRM: the operator is now AquaNetica Group Ltd, a company
// registered in England and Wales. The governing law and statutory rights
// paragraphs below follow the updated website terms (English law, Consumer
// Rights Act 2015) and a solicitor should confirm the English-law wording.
function TermsModal({ onClose }) {
  const t = useT();
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} lang="en">
        <div className="modal-header">
          {/* The legal text, its title and its link stay English in every interface language. */}
          <h2>Terms &amp; Disclaimer</h2>
          <button className="icon-btn" onClick={onClose} aria-label={t('common.close')}>×</button>
        </div>
        <div className="modal-body">
          <EnglishGoverningNote />
          <p className="legal-text">
            <strong>Service provided “as is”.</strong> AquaNetica IQ, including the Gia
            assistant and all calculators, is provided on an “as is” and “as available”
            basis, without warranties of any kind, whether express or implied, including
            as to accuracy, completeness, fitness for a particular purpose, or
            availability.
          </p>
          <p className="legal-text">
            <strong>No professional advice.</strong> The platform does not provide
            professional engineering, veterinary, legal, or financial advice, and using it
            does not create any professional or advisory relationship. Outputs are general
            information only. You must independently verify all outputs with a suitably
            qualified professional before acting on them, and you remain solely responsible
            for decisions you make.
          </p>
          <p className="legal-text">
            <strong>Limitation of liability.</strong> To the maximum extent permitted by
            law, AquaNetica Group Ltd and its suppliers will not be liable for any loss or
            damage arising from your use of, or reliance on, the platform or its outputs.
          </p>
          <p className="legal-text">
            <strong>Governing law.</strong> These terms are governed by the laws of
            England and Wales.
          </p>
          <p className="legal-text">
            <strong>Your statutory rights.</strong> Nothing in these terms excludes,
            restricts, or modifies any right or remedy you have under the UK Consumer
            Rights Act 2015, the Australian Consumer Law, or any other law that cannot
            lawfully be excluded. Where liability cannot be excluded but can be limited, our
            liability is limited to the maximum extent permitted by law.
          </p>
        </div>
      </div>
    </div>
  );
}

/*
 * Where every upgrade CTA goes. The plans and their prices live on the
 * AquaNetica pricing page; sending someone to the site's home page instead
 * makes them hunt for the thing they just asked to see. One constant so a
 * future CTA cannot quietly point somewhere else.
 */
const PRICING_URL = 'https://aquanetica.com/pricing/';

/*
 * Sign-out confirmation.
 *
 * A dialog rather than a straight action because signing out has one
 * consequence the member has to be able to choose: whether their saved
 * conversations and projects go with it. Those live only in this browser, so
 * the choice is between leaving one person's work readable on a shared machine
 * and destroying it for everyone else. Neither default is right for everybody,
 * so it is asked.
 *
 * The box starts UNTICKED on every open. Destroying the member's work is the
 * unusual case, and a remembered tick would eventually catch someone out.
 */
function SignOutModal({ onCancel, onConfirm, busy }) {
  const t = useT();
  const [clearSaved, setClearSaved] = useState(false);

  return (
    <div className="modal-backdrop" onClick={busy ? undefined : onCancel}>
      <div
        className="modal modal-narrow"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="signout-title"
      >
        <div className="modal-header">
          <h2 id="signout-title">{t('signout.title')}</h2>
          <button className="icon-btn" onClick={onCancel} disabled={busy} aria-label={t('common.close')}>×</button>
        </div>
        <div className="modal-body">
          <p className="legal-text">{t('signout.body')}</p>

          <label className="pe-consent signout-option">
            <input
              type="checkbox"
              checked={clearSaved}
              onChange={(e) => setClearSaved(e.target.checked)}
              disabled={busy}
              aria-describedby="signout-clear-warning"
            />
            <span>{t('signout.alsoClear')}</span>
          </label>
          <p className="signout-warning" id="signout-clear-warning">{t('signout.irreversible')}</p>

          <div className="modal-footer">
            <button className="btn-secondary" onClick={onCancel} disabled={busy}>{t('common.cancel')}</button>
            <button className="btn-primary" onClick={() => onConfirm(clearSaved)} disabled={busy}>
              {busy ? t('sidebar.signingOut') : t('signout.confirm')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/*
 * Upgrade prompt for a control the current tier can't use. Deliberately a plain
 * explanation plus a link out — a blocked control should say what it needs, not
 * fail silently or throw a generic error.
 */
function FeaturePrompt({ title, message, onClose }) {
  const t = useT();
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-narrow" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="icon-btn" onClick={onClose} aria-label={t('common.close')}>×</button>
        </div>
        <div className="modal-body">
          <p className="legal-text">{message}</p>
          <div className="modal-footer">
            <button className="btn-secondary" onClick={onClose}>{t('feature.notNow')}</button>
            <a
              className="btn-primary"
              href={PRICING_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('feature.seePlans')}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

// Timeline options. Visible one-tap chips rather than a dropdown: fewer taps,
// no hidden options, and nothing to type on a phone. Optional — the value is
// passed straight through to the email as a plain line.
const TIMELINE_CHOICES = ['ASAP', '1–3 months', 'Just researching'];

/*
 * Procurement enquiry, opened from the "Need help sourcing this?" link.
 *
 * Written for anyone — a farmer, a student, someone with no industry
 * background. The engineering detail Gia derived is shown as a read-only
 * summary they never have to read or edit; only name and email are required.
 * One primary action, sent as action:"quote".
 *
 * One component for all three form factors: a full-screen sheet on phones and a
 * centred card on tablet/desktop, by breakpoint alone. It reuses the --kb-inset
 * variable published by the VisualViewport effect in App(), so on phones the
 * sheet shrinks above the keyboard and the Send button stays reachable.
 */
const CONDITION_CHOICES = ['New', 'Used', 'Either'];
const BUDGET_CURRENCIES = ['USD', 'AUD', 'EUR', 'GBP', 'NZD', 'SGD'];

/*
 * ANSWER LANGUAGE SHEET. Opened from the compact toolbar control: a bottom
 * sheet on a phone, a small centred card on desktop. Choosing a language
 * closes it. Only the ANSWER changes language; everything on screen, this
 * sheet included, stays English in this pass.
 */
function LanguageSheet({ current, onChoose, onClose }) {
  const dialogRef = useRef(null);
  const t = useT();
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.sheet = '1';
    root.classList.add('sheet-open');
    // Focus the current choice so keyboard users start where they are.
    dialogRef.current?.querySelector('[aria-checked="true"]')?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
      if (e.key !== 'Tab') return;
      const focusable = Array.from(dialogRef.current?.querySelectorAll('button') || []);
      if (!focusable.length) return;
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      delete root.dataset.sheet;
      root.classList.remove('sheet-open');
    };
  }, [onClose]);

  return (
    <div className="modal-backdrop lang-backdrop" onClick={onClose}>
      <div className="modal lang-sheet" onClick={(e) => e.stopPropagation()} ref={dialogRef}
        role="dialog" aria-modal="true" aria-labelledby="lang-title" aria-describedby="lang-sub">
        <div className="modal-header">
          <h2 id="lang-title">{t('lang.title')}</h2>
          <button type="button" className="icon-btn sheet-close" onClick={onClose} aria-label={t('common.close')}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true" focusable="false"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>
          </button>
        </div>
        <p className="modal-subtitle" id="lang-sub">{t('lang.sub')}</p>
        <div className="lang-list" role="radiogroup" aria-labelledby="lang-title">
          {ANSWER_LANGUAGES.map((l, i) => (
            <button
              key={l.code}
              type="button"
              role="radio"
              aria-checked={l.code === current}
              className={'lang-option' + (l.code === current ? ' selected' : '') + (i === 0 ? ' lang-option-auto' : '')}
              onClick={() => onChoose(l.code)}
            >
              <span className="lang-native" lang={l.code === 'auto' ? undefined : l.code}>{l.code === 'auto' ? t('lang.auto') : l.native}</span>
              {l.code === current && (
                <svg className="lang-check" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function ProcurementModal({ initial, user, conversationId, onClose }) {
  // Unticked every time the modal opens: consent is given for this enquiry,
  // not remembered from a previous one.
  const [consent, setConsent] = useState(false);
  const models = Array.isArray(initial.models) ? initial.models : [];
  /*
   * Name and email are prefilled from the session token, which is verified, so
   * a signed-in member is not asked for what we already know. They stay
   * editable: someone sending on a colleague's behalf must still be able to
   * redirect the reply, and the Brevo reply-to is whatever is in this field.
   */
  const [form, setForm] = useState({
    item: initial.item || '',
    specs: initial.specs || '',
    name: user?.name || '', email: user?.email || '',
    farm: '', country: '', note: '', timeline: '',
    quantity: '', delivery_city: '', required_by: '', condition: '',
    budget_amount: '', budget_currency: 'USD', budget_unknown: false,
    company_website: '', // honeypot — must stay empty
  });
  const [showSpecFields, setShowSpecFields] = useState(false);
  const [showFarmFields, setShowFarmFields] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | sending | done | error
  const [errorMsg, setErrorMsg] = useState('');
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const dialogRef = useRef(null);
  const nameRef = useRef(null);
  const t = useT();

  /*
   * One idempotency key per modal submission, minted when the modal opens and
   * reused for every retry of that same enquiry. It is the half of duplicate
   * prevention that survives what the disabled button cannot: a retry after a
   * timeout, a flaky connection that delivered the request but lost the
   * response, a member pressing back and forward. WordPress rejects a second
   * write with the same key, so a race can cost us a response but never a
   * second row.
   */
  const idempotencyKey = useRef(
    (window.crypto?.randomUUID?.() )
    || `aqnk-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`
  );

  /*
   * The missing-info nudge. Purely deterministic: these three are what a
   * supplier needs before they can price anything, and this is a list of the
   * blank ones. No model call, no judgement.
   *
   * It is derived rather than raised on submit, so it is on screen while the
   * member is still filling the form rather than flashing past on the way to
   * the success screen. It never blocks the send: quantity and city are
   * required and validated above, so this is advice about an optional date.
   *
   * Held back until the required fields are done, so it appears at the moment
   * it is useful instead of greeting an empty form.
   */
  const missingForQuote = [
    !form.quantity.trim() && t('pe.missing.quantity'),
    !form.delivery_city.trim() && t('pe.missing.city'),
    !form.required_by.trim() && t('pe.missing.date'),
  ].filter(Boolean);

  const readyToSend = form.name.trim() && form.email.trim() && form.quantity.trim() && form.delivery_city.trim();
  const nudge = readyToSend && missingForQuote.length
    ? t('pe.nudge', { list: missingForQuote.join(', ') })
    : '';

  /*
   * Desktop dialog behaviour: autofocus the first real field, Esc closes, and
   * Tab is trapped inside so keyboard users can't land on the page behind.
   * The honeypot is display:none so it never appears in the tab order.
   */
  useEffect(() => {
    nameRef.current?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
      if (e.key !== 'Tab') return;
      const el = dialogRef.current;
      if (!el) return;
      const focusable = Array.from(el.querySelectorAll(
        'a[href], button:not([disabled]), input:not([type="hidden"]), textarea, select, [tabindex]:not([tabindex="-1"])'
      )).filter((n) => n.type !== 'hidden' && n.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  /*
   * THE ON-SCREEN KEYBOARD, the same way the chat handles it.
   *
   * The chat's VisualViewport effect already publishes --kb-inset (how much of
   * the layout viewport the keyboard covers) and --vv-top (where the visible
   * rectangle starts once iOS has shifted it to reveal a field). The sheet is
   * pinned to exactly that rectangle in CSS, so its header and close button
   * stay on screen and its body shrinks above the keyboard. What is left here:
   *
   *  - data-sheet on <html> tells the chat not to scroll itself while the
   *    sheet owns the screen, and .sheet-open stops the page behind from
   *    scrolling, so iOS has one scroller to move rather than two;
   *  - after the keyboard settles, the focused field is brought into view
   *    INSIDE the sheet body (scrolling the body, never the page);
   *  - when a field blurs and nothing else takes focus, the keyboard geometry
   *    is re-read once it has finished closing. iOS does not always fire a
   *    final visualViewport resize, which left the sheet stuck short;
   *  - on close, the page behind is put back exactly where it was.
   */
  useEffect(() => {
    const root = document.documentElement;
    const savedY = window.scrollY;
    root.dataset.sheet = '1';
    root.classList.add('sheet-open');
    const vv = window.visualViewport;
    let settle = 0, frame = 0;

    const revealFocused = () => {
      const el = document.activeElement;
      const sheet = dialogRef.current;
      if (!el || !sheet || !sheet.contains(el) || !/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) return;
      const body = sheet.querySelector('.modal-body');
      if (!body) return;
      const b = body.getBoundingClientRect();
      const f = el.getBoundingClientRect();
      const margin = 16;
      if (f.bottom > b.bottom - margin) body.scrollTop += f.bottom - (b.bottom - margin);
      else if (f.top < b.top + margin) body.scrollTop -= (b.top + margin) - f.top;
    };
    // Wait for the sheet to take its new height (CSS transition, ~240ms)
    // before measuring where the field is.
    const later = () => {
      if (settle) clearTimeout(settle);
      settle = setTimeout(() => { frame = requestAnimationFrame(revealFocused); }, 280);
    };
    const onFocusIn = () => later();
    const onFocusOut = () => {
      setTimeout(() => {
        const a = document.activeElement;
        if (a && dialogRef.current && dialogRef.current.contains(a) && /^(INPUT|TEXTAREA|SELECT)$/.test(a.tagName)) return;
        // Keyboard is on its way down: re-read the geometry through the chat's
        // own listener once it has closed, the same code path as a real event.
        setTimeout(() => { if (vv) vv.dispatchEvent(new Event('resize')); }, 350);
      }, 60);
    };
    const sheet = dialogRef.current;
    sheet?.addEventListener('focusin', onFocusIn);
    sheet?.addEventListener('focusout', onFocusOut);
    vv?.addEventListener('resize', later);

    return () => {
      sheet?.removeEventListener('focusin', onFocusIn);
      sheet?.removeEventListener('focusout', onFocusOut);
      vv?.removeEventListener('resize', later);
      if (settle) clearTimeout(settle);
      if (frame) cancelAnimationFrame(frame);
      delete root.dataset.sheet;
      root.classList.remove('sheet-open');
      if (Math.abs(window.scrollY - savedY) > 1) window.scrollTo(0, savedY);
    };
  }, []);

  const submit = async () => {
    if (status === 'sending') return;
    // Plain-language validation, in the order the fields appear.
    if (!form.name.trim()) { setErrorMsg(t('pe.err.name')); setStatus('error'); return; }
    if (!form.email.trim()) { setErrorMsg(t('pe.err.email')); setStatus('error'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setErrorMsg(t('pe.err.emailInvalid')); setStatus('error'); return;
    }
    if (!form.quantity.trim()) { setErrorMsg(t('pe.err.quantity')); setStatus('error'); return; }
    if (!form.delivery_city.trim()) { setErrorMsg(t('pe.err.city')); setStatus('error'); return; }

    setStatus('sending'); setErrorMsg('');
    try {
      const res = await fetch('/api/procurement-enquiry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          recommended_models: models,
          action: 'quote', // single primary action; no quote/procure choice
          consent: true,
          consent_at: new Date().toISOString(),
          consent_terms_version: TERMS_VERSION,
          conversationId: conversationId || '',
          triggering_question: initial.question || '',
          idempotency_key: idempotencyKey.current,
          // Lower-cased for storage; the chips are capitalised for reading.
          condition: form.condition ? form.condition.toLowerCase() : '',
        }),
      });
      if (!res.ok) {
        // A message from the server is shown as the server wrote it (English).
        let msg = t('pe.err.send');
        try { const j = await res.json(); if (j && j.message) msg = j.message; } catch { /* keep default */ }
        setErrorMsg(msg); setStatus('error'); return;
      }
      setStatus('done');
    } catch (e) {
      setErrorMsg(t('pe.err.network'));
      setStatus('error');
    }
  };

  return (
    <div className="modal-backdrop procurement-backdrop" onClick={onClose}>
      <div
        className="modal procurement-modal"
        onClick={e => e.stopPropagation()}
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pe-title"
      >
        <div className="modal-header">
          <h2 id="pe-title">{t('pe.title')}</h2>
          {/*
            A 44px target on its own layer at the top of the sheet. The glyph
            is drawn as an SVG so its hit area is the button, not a text box
            whose metrics vary by font.
          */}
          <button type="button" className="icon-btn pe-close" onClick={onClose} aria-label={t('common.close')}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true" focusable="false"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>
          </button>
        </div>

        {status === 'done' ? (
          <div className="modal-body">
            <p className="legal-text">
              {tNodes(t, 'pe.thanks', { email: <strong>{form.email}</strong> })}
            </p>
            <div className="modal-footer">
              <button className="btn-primary" onClick={onClose}>{t('pe.done')}</button>
            </div>
          </div>
        ) : (
          <div className="modal-body">
            {/* Honeypot: hidden from users; bots that fill it are rejected. */}
            <input
              type="text" name="company_website" tabIndex={-1} autoComplete="off"
              value={form.company_website} onChange={update('company_website')}
              // display:none (not off-screen): password managers and iOS/Safari
              // autofill ignore autocomplete="off" and WILL fill an off-screen
              // field named "company_website", which trips the honeypot and
              // silently drops real enquiries. display:none is skipped by autofill.
              style={{ display: 'none' }}
              aria-hidden="true"
            />

            {/*
              The engineering detail, as plain read-only text. A non-technical
              user can send the request without reading or touching any of it;
              "Edit details" is there for those who want to correct it.
            */}
            <section className="pe-summary" aria-label={t('pe.whatYouNeed')}>
              <h3 className="pe-summary-title">{t('pe.whatYouNeed')}</h3>
              <p className="pe-summary-item">{form.item || t('pe.equipment')}</p>
              {form.specs && <p className="pe-summary-specs">{form.specs}</p>}
              {models.length > 0 && (
                <p className="pe-summary-models">
                  <span className="pe-summary-label">{t('pe.suggestedModels')}</span> {models.join(', ')}
                </p>
              )}
              <button
                type="button"
                className="pe-reveal"
                aria-expanded={showSpecFields}
                onClick={() => setShowSpecFields(v => !v)}
              >
                {showSpecFields ? t('pe.hideDetails') : t('pe.editDetails')}
              </button>

              {showSpecFields && (
                <div className="pe-reveal-body">
                  <div className="field">
                    {/* Distinct from the section's own "What you need" name, so
                        screen readers don't announce two things identically. */}
                    <label htmlFor="pe-item">{t('pe.item')}</label>
                    <input id="pe-item" value={form.item} onChange={update('item')} />
                  </div>
                  <div className="field">
                    <label htmlFor="pe-specs">{t('pe.specs')}</label>
                    <textarea id="pe-specs" rows={3} value={form.specs} onChange={update('specs')} />
                  </div>
                </div>
              )}
            </section>

            <div className="field">
              <label htmlFor="pe-name">{t('pe.name')}</label>
              <input id="pe-name" ref={nameRef} value={form.name} onChange={update('name')} autoComplete="name" />
            </div>
            <div className="field">
              <label htmlFor="pe-email">{t('pe.email')}</label>
              <input id="pe-email" type="email" value={form.email} onChange={update('email')} autoComplete="email" inputMode="email" />
            </div>

            {/* One-tap chips: nothing to type, nothing hidden behind a dropdown. */}
            <div className="field">
              <span className="pe-chip-label" id="pe-timeline-label">{t('pe.when')}</span>
              <div className="pe-chips" role="group" aria-labelledby="pe-timeline-label">
                {TIMELINE_CHOICES.map((choice) => (
                  <button
                    key={choice}
                    type="button"
                    className={'pe-chip' + (form.timeline === choice ? ' selected' : '')}
                    aria-pressed={form.timeline === choice}
                    // Tapping the selected chip clears it — it is optional, so
                    // there must be a way back to "not answered".
                    onClick={() => setForm(f => ({ ...f, timeline: f.timeline === choice ? '' : choice }))}
                  >
                    {t('pe.timeline.' + choice)}
                  </button>
                ))}
              </div>
            </div>

            {/*
              What a supplier needs before they can price anything. Quantity and
              city are required; the rest sharpen the quote without blocking it.
            */}
            <div className="field">
              <label htmlFor="pe-quantity">{t('pe.quantity')}</label>
              <input
                id="pe-quantity" value={form.quantity} onChange={update('quantity')}
                placeholder={t('pe.quantityPlaceholder')}
              />
            </div>
            <div className="field">
              <label htmlFor="pe-city">{t('pe.city')}</label>
              <input
                id="pe-city" value={form.delivery_city} onChange={update('delivery_city')}
                autoComplete="address-level2" placeholder={t('pe.cityPlaceholder')}
              />
            </div>
            <div className="field">
              <label htmlFor="pe-required-by">{t('pe.neededBy')}</label>
              <input
                id="pe-required-by" type="date" value={form.required_by}
                onChange={update('required_by')}
              />
            </div>

            <div className="field">
              <span className="pe-chip-label" id="pe-condition-label">{t('pe.condition')}</span>
              <div className="pe-chips" role="group" aria-labelledby="pe-condition-label">
                {CONDITION_CHOICES.map((choice) => (
                  <button
                    key={choice}
                    type="button"
                    className={'pe-chip' + (form.condition === choice ? ' selected' : '')}
                    aria-pressed={form.condition === choice}
                    // Tapping the selected chip clears it, same as the timeline.
                    onClick={() => setForm(f => ({ ...f, condition: f.condition === choice ? '' : choice }))}
                  >
                    {t('pe.condition.' + choice)}
                  </button>
                ))}
              </div>
            </div>

            <div className="field">
              <span className="pe-chip-label" id="pe-budget-label">{t('pe.budget')}</span>
              <div className="pe-budget" role="group" aria-labelledby="pe-budget-label">
                <input
                  id="pe-budget-amount"
                  type="number" min="0" step="any" inputMode="decimal"
                  className="pe-budget-amount"
                  value={form.budget_amount}
                  onChange={update('budget_amount')}
                  disabled={form.budget_unknown}
                  placeholder={t('pe.amount')}
                  aria-label={t('pe.budgetAmount')}
                />
                <select
                  className="pe-budget-currency"
                  value={form.budget_currency}
                  onChange={update('budget_currency')}
                  disabled={form.budget_unknown}
                  aria-label={t('pe.budgetCurrency')}
                >
                  {BUDGET_CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <button
                  type="button"
                  className={'pe-chip' + (form.budget_unknown ? ' selected' : '')}
                  aria-pressed={form.budget_unknown}
                  // "Not sure" is a real answer, not an empty one: it tells the
                  // supplier to lead with options rather than wait for a figure.
                  onClick={() => setForm(f => ({
                    ...f,
                    budget_unknown: !f.budget_unknown,
                    budget_amount: f.budget_unknown ? f.budget_amount : '',
                  }))}
                >
                  {t('pe.notSure')}
                </button>
              </div>
            </div>

            <div className="field">
              <label htmlFor="pe-note">{t('pe.note')}</label>
              <textarea
                id="pe-note" rows={3} value={form.note} onChange={update('note')}
                placeholder={t('pe.notePlaceholder')}
              />
            </div>

            <button
              type="button"
              className="pe-reveal"
              aria-expanded={showFarmFields}
              onClick={() => setShowFarmFields(v => !v)}
            >
              {showFarmFields ? t('pe.hideFarm') : t('pe.addFarm')}
            </button>
            {showFarmFields && (
              <div className="pe-reveal-body">
                <div className="field">
                  <label htmlFor="pe-farm">{t('pe.farm')}</label>
                  <input id="pe-farm" value={form.farm} onChange={update('farm')} autoComplete="organization" />
                </div>
                <div className="field">
                  <label htmlFor="pe-country">{t('pe.country')}</label>
                  <input id="pe-country" value={form.country} onChange={update('country')} autoComplete="country-name" />
                </div>
              </div>
            )}

            {nudge && <p className="pe-nudge">{nudge}</p>}
            {status === 'error' && <p className="procurement-error" role="alert">{errorMsg}</p>}

            {/*
              * Consent sits with the button it gates, unticked on every open.
              * The two documents are English and open in a new tab, so nobody
              * loses a half-filled form to read them.
              */}
            <label className="pe-consent">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                aria-describedby="pe-consent-text"
              />
              <span id="pe-consent-text">
                {tNodes(t, 'pe.consent', {
                  terms: (
                    <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">{t('pe.consentTerms')}</a>
                  ),
                  privacy: (
                    <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">{t('pe.consentPrivacy')}</a>
                  ),
                })}
              </span>
            </label>

            <div className="modal-footer pe-footer">
              <button className="btn-primary pe-send" onClick={submit} disabled={!consent || status === 'sending'}>
                {status === 'sending' ? t('pe.sending') : t('pe.send')}
              </button>
              <p className="pe-reassurance">{t('pe.reassurance')}</p>
            </div>

            {/* The two obligation and liability notices: English in every interface language. */}
            <EnglishGoverningNote className="legal-governing procurement-governing" />
            <div className="procurement-notice" lang="en">
              AquaNetica charges a sourcing fee on completed procurement, quoted to you in
              writing before you commit to any purchase. You are under no obligation until
              you accept that quote.
            </div>
            <div className="procurement-notice" lang="en">
              AquaNetica facilitates sourcing and introductions only. We do not manufacture,
              supply or warrant any equipment. All specifications, warranties, guarantees and
              fitness for purpose remain with the supplier or manufacturer.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Conversation store + garbage collection                             */
/* ------------------------------------------------------------------ */
/*
 * All conversations live in ONE global localStorage store, so every tab lists
 * the full history. The ONLY per-tab state is which conversation is currently
 * open, held in sessionStorage — it survives a refresh of that tab and dies
 * with the tab, so a new tab opens a fresh empty chat while still showing the
 * whole sidebar of saved conversations.
 *
 * Writes touch a single conversation record (read-modify-write of the store),
 * never a whole-store blob, so a write from one tab can't drop a conversation
 * another tab just created; if the same conversation is open in two tabs, last
 * write wins. Attached files stay keyed to the conversation id in IndexedDB, so
 * a file follows its conversation into whichever tab opens it.
 */
const CONV_KEY = 'aqua-iq-conversations';        // global store (array of convs)
const ACTIVE_CONV_KEY = 'aqua-iq-active-conv';   // per-tab: sessionStorage id
const LEGACY_BUCKET_PREFIX = 'aqua-iq-conv::';         // pre-global per-tab buckets
const LEGACY_BUCKET_ACTIVE_PREFIX = 'aqua-iq-active-conv::'; // pre-global per-tab active
const CONV_TTL_MS = 7 * 24 * 60 * 60 * 1000; // prune conversations idle > 7 days

// Sentinel for "no conversation handled yet". A Symbol so it can never collide
// with a real conversation id OR with the null of the empty state — both of
// which are legitimate values the scroll effects must be able to react to.
const NO_CONVERSATION = Symbol('no-conversation');

/* ------------------------------------------------------------------ */
/* Tier entitlements and metered usage — SERVER-OWNED                  */
/* ------------------------------------------------------------------ */
/*
 * Limits, remaining counts and feature access all come from the server
 * (GET /api/entitlements, refreshed by the `usage` event on the chat stream).
 * Nothing here is stored in localStorage: the browser is not the source of
 * truth for what a member has paid for, and the old localStorage counter could
 * be reset by clearing site data.
 *
 * ONLY the AI is gated. Chat questions and file messages are metered; saved
 * conversations and the calculators are free and unlimited on every tier
 * including Free. Upload-turns count per MESSAGE carrying a file, not per
 * document — the file is re-sent on each follow-up while it stays attached.
 *
 * The client never decides entitlement — it only renders what the server says
 * and reacts to a 402. Every gate here is a UX affordance mirroring a rule the
 * server enforces independently.
 */

// Until /api/entitlements answers (and if it never does) assume full access:
// fail open, exactly as the server does. A paying member must never be locked
// out of a control by a slow or failed entitlements fetch.
/*
 * The names customers see for each tier, as the server's entitlements use them
 * (backend/entitlements.js). The session's own tier_label comes from WordPress
 * and carries the internal names (Operator, Partner), so it is never shown.
 * Used only until /api/entitlements answers with its own label.
 */
const CUSTOMER_TIER_LABELS = { navigator: 'Free', operator: 'Pro', partner: 'Max' };

const OPEN_FEATURES = {
  calculators: true,
  file_upload: true,
};

// Counter turns amber at or below ~20% of the tier's allowance (never < 1), so
// it reads as a warning state rather than the normal one.
export function lowThreshold(limit) {
  return Math.max(1, Math.floor((Number(limit) || 0) * 0.2));
}

// Where the landing screen sends users to sign in: the WordPress /iq-login
// bridge (mints an SSO token for a logged-in member; bounces a logged-out
// visitor to the branded signup page). NOT wp-login.php — that dead-ends in
// wp-admin with no token. ?return=iq tells the bridge to send the visitor back
// to /iq-login after authenticating, so someone who started in the IQ app
// lands back in the app rather than on the community feed. Overridable at
// build time via REACT_APP_IQ_LOGIN_URL (CRA bakes env vars into the bundle,
// so changing it means rebuilding).
const LOGIN_URL = process.env.REACT_APP_IQ_LOGIN_URL || 'https://aquanetica.com/iq-login?return=iq';

/*
 * Where signing out sends the browser. The mirror image of LOGIN_URL: a
 * WordPress route that ends the WordPress session and lands the member on the
 * public IQ page.
 *
 * Clearing IQ's own cookie is not signing out. Identity here comes from
 * aquanetica.com, so a member whose WordPress session is still alive is one
 * redirect away from a fresh token, which is exactly what used to happen, and
 * why Sign out appeared to do nothing at all. This URL is the fallback only:
 * /auth/logout answers with the destination it wants used, so the server can
 * repoint it without a rebuild (CRA bakes env vars into the bundle).
 */
const SIGNOUT_URL = process.env.REACT_APP_IQ_LOGOUT_URL || 'https://aquanetica.com/iq-logout';

/*
 * What signing out removes from this browser, and what it deliberately leaves
 * alone.
 *
 * Conversations, projects and saved calculator results are the member's own
 * record of their design work. They exist NOWHERE ELSE: IQ keeps no
 * server-side copy, so clearing them on every sign-out would mean signing out
 * on Friday and back in on Monday costs someone the lot, silently, with
 * nothing to restore from. They stay by default. The sign-out dialog offers to
 * clear them for the shared-device case, unticked, with the consequence said
 * plainly next to the box.
 *
 * TWO THINGS GO EVERY TIME, because neither is a loss:
 *   uploaded documents  the heaviest and most private thing on the device,
 *                       whole PDFs and drawings, and re-attachable any time
 *   message ratings     a private judgement on individual answers
 *
 * Nothing here touches the monthly question and upload counts. Those are
 * server-side, counted against the WordPress account, and signing out is not
 * a way to get more of them.
 *
 * Every step is wrapped, because storage throws rather than returns in private
 * mode and under blocked-cookie settings, and a failure to tidy up must never
 * stop the sign-out itself. IndexedDB is handled by the caller, which can
 * await it.
 */
const RATINGS_KEY = 'aqua-iq-ratings';

/*
 * A dead counter from before metering moved to the server. Nothing writes it
 * any more, so any copy still on a device is a leftover, and a key called
 * "usage" surviving a sign-out looks exactly like an allowance being carried
 * around in the browser. It is not. It goes.
 */
const STALE_USAGE_KEY = 'aqua-iq-usage';

export function clearSignOutData() {
  [RATINGS_KEY, STALE_USAGE_KEY].forEach((key) => {
    try { localStorage.removeItem(key); } catch { /* storage unavailable */ }
  });
}

/*
 * The ticked-box path: everything the member has built up in this browser.
 *
 * IRREVERSIBLE, and the dialog says so before it runs.
 *
 * WRITTEN AS "EVERYTHING EXCEPT", NOT AS A LIST. A list was tried and was
 * wrong within the hour: it named conversations, projects and calc-results,
 * and missed aqua-iq-calc-worksheet (the calculator inputs, the largest thing
 * on a real device) and aqua-iq-active-proj, both of which are as much the
 * member's work as the rest. A member who ticks a box that says "clear my
 * saved conversations and projects" and is left with their calculator sheet
 * behind has been told something untrue. Anything added later is now cleared
 * by default, which is the safe direction for this particular box.
 *
 * The two exceptions:
 *   aqua-iq-sidebar-hidden      how this device is set up, not who used it
 *   aqua-iq-answer-lang:<sub>   keyed per account, so it cannot leak to the
 *                               next person, and a returning member gets their
 *                               own language back
 *
 * The answer-language LAST-ACCOUNT hint is not an exception: it holds the id
 * of the member who just left, which on a shared device is the one thing worth
 * removing.
 */
export const KEPT_ON_CLEAR = /^aqua-iq-(sidebar-hidden|answer-lang:(?!last-account).+)$/;

export function clearSavedWork() {
  try {
    const doomed = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith('aqua-iq') && !KEPT_ON_CLEAR.test(key)) doomed.push(key);
    }
    doomed.forEach(key => { try { localStorage.removeItem(key); } catch { /* ignore */ } });
  } catch { /* storage unavailable */ }

  // The open conversation is per-tab state, and its conversation is gone.
  try {
    const doomed = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('aqua-iq')) doomed.push(key);
    }
    doomed.forEach(key => { try { sessionStorage.removeItem(key); } catch { /* ignore */ } });
  } catch { /* storage unavailable */ }
}

function parseConvs(raw) {
  try { const v = JSON.parse(raw); return Array.isArray(v) ? v : []; } catch { return []; }
}

function readStore() {
  try { return parseConvs(localStorage.getItem(CONV_KEY)); } catch { return []; }
}

function writeStore(convs) {
  try { localStorage.setItem(CONV_KEY, JSON.stringify(convs)); } catch { /* quota/serialize */ }
}

// Idle timestamp for the TTL: lastActive if present, otherwise createdAt so a
// conversation from before inactivity tracking is treated as last active when
// it was created.
function convLastActive(c) {
  if (!c) return 0;
  const la = Number(c.lastActive);
  if (Number.isFinite(la) && la > 0) return la;
  return Number(c.createdAt) || 0;
}

// Backfill lastActive from createdAt on conversations that predate inactivity
// tracking, so it persists once written back.
function normalizeConvs(convs) {
  return convs.map(c => (c && c.lastActive == null ? { ...c, lastActive: Number(c.createdAt) || 0 } : c));
}

// Keep conversations idle for less than the TTL (by lastActive, not age).
function freshConvs(convs, now) {
  const cutoff = now - CONV_TTL_MS;
  return normalizeConvs(convs).filter(c => convLastActive(c) >= cutoff);
}

function collectKeys(prefix) {
  const keys = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(prefix)) keys.push(k);
    }
  } catch { /* ignore */ }
  return keys;
}

// Load the global store, merging any pre-global per-tab buckets into it on first
// run (dedupe by conversation id, newest lastActive wins — nothing lost), then
// remove the legacy bucket keys. Writes the merged store UNPRUNED so a later
// gcConversations can still find idle conversations and delete their attached
// files, but returns the pruned list (newest-first) for display so the sidebar
// never flashes stale entries. Idempotent: a second run finds no buckets and
// just re-reads the store.
function loadConversations(now) {
  try {
    const byId = new Map();
    const consider = (arr) => {
      for (const c of normalizeConvs(arr)) {
        if (!c || !c.id) continue;
        const prev = byId.get(c.id);
        if (!prev || convLastActive(c) >= convLastActive(prev)) byId.set(c.id, c);
      }
    };
    consider(readStore());
    const bucketKeys = collectKeys(LEGACY_BUCKET_PREFIX);
    bucketKeys.forEach(k => consider(parseConvs(localStorage.getItem(k))));

    const merged = [...byId.values()].sort((a, b) => convLastActive(b) - convLastActive(a));
    writeStore(merged);
    // Retire the pre-global per-tab keys now that everything is merged.
    bucketKeys.forEach(k => localStorage.removeItem(k));
    collectKeys(LEGACY_BUCKET_ACTIVE_PREFIX).forEach(k => localStorage.removeItem(k));
    return freshConvs(merged, now);
  } catch { return readStore(); }
}

// Which conversation is open in THIS tab (sessionStorage). A brand-new tab has
// none, so it opens a fresh empty chat. On first load after the upgrade, adopt
// a pre-global localStorage active pointer into this session so the currently
// open chat is not lost, then clear it so genuinely new tabs still open empty.
function loadActiveConv() {
  try {
    const id = sessionStorage.getItem(ACTIVE_CONV_KEY);
    if (id) return id;
    const legacy = localStorage.getItem(ACTIVE_CONV_KEY);
    if (legacy) {
      sessionStorage.setItem(ACTIVE_CONV_KEY, legacy);
      localStorage.removeItem(ACTIVE_CONV_KEY);
      return legacy;
    }
    return null;
  } catch { return null; }
}

// Upsert a single conversation record: re-read the store, replace it by id (or
// prepend if new), write back. Never rewrites other tabs' records.
function upsertConvInStore(conv) {
  if (!conv || !conv.id) return;
  const arr = readStore();
  const idx = arr.findIndex(c => c && c.id === conv.id);
  if (idx >= 0) arr[idx] = conv; else arr.unshift(conv);
  writeStore(arr);
}

// Remove conversation records by id from the store (read-modify-write).
function removeConvsFromStore(ids) {
  const drop = new Set(ids);
  writeStore(readStore().filter(c => c && !drop.has(c.id)));
}

// Prune conversations idle past the TTL from the global store and best-effort
// delete the IndexedDB files of the removed ones.
function gcConversations(now, onOrphanFile) {
  const arr = readStore();
  const kept = freshConvs(arr, now);
  if (kept.length === arr.length) return kept;
  const keptIds = new Set(kept.map(c => c.id));
  arr.forEach(c => { if (c && c.id && !keptIds.has(c.id) && onOrphanFile) onOrphanFile(c.id); });
  writeStore(kept);
  return kept;
}

function App() {
  // A single "now" shared by load and GC so both agree on the TTL cutoff.
  // Lazy ref so it is computed once per mount.
  const sessionRef = useRef(null);
  if (!sessionRef.current) sessionRef.current = { now: Date.now() };

  const [conversations, setConversations] = useState(() => loadConversations(sessionRef.current.now));
  const [projects, setProjects] = useState(() => {
    try {
      const saved = localStorage.getItem('aqua-iq-projects');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [activeConvId, setActiveConvId] = useState(() => loadActiveConv());
  const [activeProjectId, setActiveProjectId] = useState(() => localStorage.getItem('aqua-iq-active-proj') || null);
  const [expandedProjects, setExpandedProjects] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  // Synchronous twin of `loading`: see streamAnswer. Guards against a charged duplicate turn.
  const inFlightRef = useRef(false);
  // Index of the message currently being streamed into (null when idle), so the
  // action toolbar can be hidden on that one answer only.
  const [busyIndex, setBusyIndex] = useState(null);
  // Which stage the branded loader shows: 'searching' (retrieval running),
  // 'found' (retrieval returned, first token pending), 'writing' (streaming),
  // or null when idle. Driven by the actual request lifecycle in streamAnswer.
  const [loadPhase, setLoadPhase] = useState(null);
  const [copiedIndex, setCopiedIndex] = useState(null);
  // { [convId]: { [msgIndex]: 'up' | 'down' } } — local only, no backend yet.
  const [ratings, setRatings] = useState(() => {
    try { return JSON.parse(localStorage.getItem('aqua-iq-ratings') || '{}'); } catch { return {}; }
  });
  // Tier entitlements + metered remaining, straight from the server. null until
  // /api/entitlements answers; treated as full access while unknown (fail open).
  const [ent, setEnt] = useState(null);
  // Trial code entry: closed until asked for, and never remembered.
  const [codeOpen, setCodeOpen] = useState(false);
  const [codeValue, setCodeValue] = useState('');
  const [codeState, setCodeState] = useState(null); // 'checking' | 'invalid' | 'error' | 'done'
  /*
   * Set when the server refuses a turn: a 402 (tier limit or blocked feature)
   * or the 429 per-IP daily backstop. Holds the server's own payload
   * { error, tier_label, limit, used, message } so the wall can state exactly
   * which limit was hit without the client recomputing anything.
   */
  const [blocked, setBlocked] = useState(null);
  // Auth status from /auth/me. Optimistically "enabled: false" (today's open
  // behaviour) so the app shows immediately; the landing screen only appears if
  // the server reports auth enabled AND no session. Unreachable endpoint => stay
  // open (fail safe, never lock users out).
  const [auth, setAuth] = useState({ enabled: false, authenticated: true, user: null });
  const [dark, setDark] = useState(false);
  const [calcTick, setCalcTick] = useState(0);
  const [attachment, setAttachment] = useState(null);
  // The language Gia answers in, per user, per device (answerLanguages.js).
  const [answerLang, setAnswerLang] = useState(() => loadInitialAnswerLanguage());
  // Whether /auth/me has answered (success or failure). Until it has, the
  // member is unknown and a language choice is held rather than saved.
  const [authResolved, setAuthResolved] = useState(false);
  const pendingLangRef = useRef(null);
  /*
   * The interface follows the picker only where a translation exists
   * (i18n/index.jsx): Español gives a Spanish interface; Auto and the other
   * languages keep it English while still changing Gia's answer.
   */
  /*
   * One picker, two buckets. The interface follows the picked language in all
   * eight; the calculators are English or Spanish only, so another language
   * gets them in English, or in Spanish when the browser is Spanish
   * (i18n/index.jsx). Auto follows the browser for both.
   */
  const { uiLang, calcLang } = useMemo(() => resolveLanguages(answerLang), [answerLang]);
  const t = useMemo(() => makeT(uiLang, calcLang), [uiLang, calcLang]);
  const tc = useMemo(() => makeTc(calcLang), [calcLang]);
  const num = useMemo(() => makeNum(uiLang), [uiLang]);
  const i18n = useMemo(() => ({ t, tc, num, uiLang, calcLang }), [t, tc, num, uiLang, calcLang]);
  useEffect(() => { document.documentElement.lang = uiLang; }, [uiLang]);
  const [showLangSheet, setShowLangSheet] = useState(false);
  const langButtonRef = useRef(null);
  // Drag-and-drop upload: overlay visibility + a depth counter so child
  // elements firing dragleave don't flicker the overlay off.
  const [dragActive, setDragActive] = useState(false);
  const dragDepth = useRef(0);
  // Liability disclaimer surfaces (lawyer-review pending).
  const [showHelp, setShowHelp] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  // Procurement enquiry: { item, specs } prefill when the modal is open, else null.
  const [procurement, setProcurement] = useState(null);
  // Upgrade prompt for a control this tier can't use: { title, message }. Shown
  // instead of letting the action fail silently (or fail at the server).
  const [featurePrompt, setFeaturePrompt] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarHidden, setSidebarHidden] = useState(() => localStorage.getItem('aqua-iq-sidebar-hidden') === '1');
  const [activeView, setActiveView] = useState('chat');
  /*
   * A saved calculation the user asked to edit. Handed to the calculator,
   * which loads the values and clears it. Recalculating produces a NEW
   * worksheet entry, so saving it adds a second saved calculation and the
   * original is left untouched — these are design records and something may
   * already have been built to the original figure.
   */
  const [pendingEdit, setPendingEdit] = useState(null);
  const inputRef = useRef(null);
  const fileRef = useRef(null);
  const endRef = useRef(null);
  // The fixed composer, measured so .content can reserve exactly its height.
  const footerRef = useRef(null);
  // The chat toolbar, measured the same way for the top edge.
  const topbarRef = useRef(null);
  // Rendered answer nodes by message index, read at copy time so the clipboard
  // gets the real markup rather than a second render of the markdown.
  const answerRefs = useRef({});
  // Tracks which conversation's file currently lives in `attachment`, so the
  // load-on-switch effect doesn't clobber a file we just picked/created.
  const loadedFileConvRef = useRef(null);

  // Persist conversation edits one record at a time. Only the active
  // conversation is ever mutated (create/stream/retry/file-bind), so upserting
  // just that record re-reads the store and can't drop a conversation another
  // tab created; deletions are handled explicitly by their handlers.
  useEffect(() => {
    const active = conversations.find(c => c.id === activeConvId);
    if (active) upsertConvInStore(active);
  }, [conversations, activeConvId]);
  useEffect(() => { localStorage.setItem('aqua-iq-projects', JSON.stringify(projects)); }, [projects]);
  // The open conversation is per-tab state: sessionStorage, so a new tab starts
  // with none (fresh empty chat) while the sidebar still lists all history.
  useEffect(() => {
    try {
      if (activeConvId) sessionStorage.setItem(ACTIVE_CONV_KEY, activeConvId);
      else sessionStorage.removeItem(ACTIVE_CONV_KEY);
    } catch {}
  }, [activeConvId]);
  // A restored per-tab active id may point at a conversation another tab deleted
  // or the GC pruned; drop it so we open the empty state, not a dead chat.
  useEffect(() => {
    if (activeConvId && !conversations.some(c => c.id === activeConvId)) setActiveConvId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Garbage-collect on load: prune conversations idle past the TTL from the
  // global store, delete their attached files, and sync the pruned list into
  // state so the sidebar doesn't show stale entries.
  useEffect(() => {
    const kept = gcConversations(sessionRef.current.now, (id) => { deleteFile(id).catch(() => {}); });
    setConversations(prev => (kept.length === prev.length ? prev : kept));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (activeProjectId) localStorage.setItem('aqua-iq-active-proj', activeProjectId);
    else localStorage.removeItem('aqua-iq-active-proj');
  }, [activeProjectId]);
  useEffect(() => { localStorage.setItem('aqua-iq-sidebar-hidden', sidebarHidden ? '1' : '0'); }, [sidebarHidden]);
  useEffect(() => { localStorage.setItem('aqua-iq-ratings', JSON.stringify(ratings)); }, [ratings]);
  // Tier entitlements + remaining counts. Server-owned; nothing is cached in
  // localStorage. On any failure `ent` stays null, which reads as full access
  // (fail open) and hides the counters rather than showing a wrong number.
  /*
   * A failed fetch (a deploy swapping instances, a slow WordPress lookup) used
   * to leave `ent` null for the whole page view: the sidebar lost its counter,
   * Manage plan and Have a code?, and the plan name fell back to the session's
   * internal label. So a failure now retries a few times, spaced out, and the
   * sidebar fills in as soon as one succeeds.
   */
  const entRetryRef = useRef(null);
  const refreshEnt = useCallback((attempt = 0) => {
    clearTimeout(entRetryRef.current);
    const retry = () => {
      const delays = [2000, 5000, 10000];
      if (attempt < delays.length) entRetryRef.current = setTimeout(() => refreshEnt(attempt + 1), delays[attempt]);
    };
    try {
      return Promise.resolve(fetch('/api/entitlements'))
        .then(r => (r.ok ? r.json() : null))
        .then(d => { if (d && typeof d === 'object' && d.tier) setEnt(d); else retry(); })
        .catch(() => { retry(); });
    } catch { /* fetch unavailable (e.g. tests) — stay open */ }
    return Promise.resolve();
  }, []);
  useEffect(() => () => clearTimeout(entRetryRef.current), []);
  /*
   * Fetched once on mount and again whenever the tab comes back to the front.
   * The refetch matters because entitlements can change with nobody touching
   * THIS tab: a trial code redeemed on the phone, a plan bought on the site, a
   * trial lapsing overnight. Without it a tab left open keeps gating on what
   * was true when it loaded, which looks exactly like the UI ignoring the
   * server. Cheap (one small GET, only when the tab is shown) and the server
   * stays the only authority either way.
   */
  useEffect(() => {
    refreshEnt();
    const onShow = () => { if (document.visibilityState === 'visible') refreshEnt(); };
    document.addEventListener('visibilitychange', onShow);
    window.addEventListener('focus', onShow);
    return () => {
      document.removeEventListener('visibilitychange', onShow);
      window.removeEventListener('focus', onShow);
    };
  }, [refreshEnt]);
  // Ask the server whether auth is enabled and whether we have a session. On any
  // error (endpoint missing, offline) leave the optimistic open state so nobody
  // is locked out — the gate that actually protects data is server-side on /api.
  useEffect(() => {
    let alive = true;
    try {
      Promise.resolve(fetch('/auth/me'))
        .then(r => r.json())
        .then(d => { if (alive && d && typeof d === 'object') setAuth(d); })
        .catch(() => {})
        .finally(() => { if (alive) setAuthResolved(true); });
    } catch { setAuthResolved(true); /* fetch unavailable (e.g. tests) — stay open */ }
    return () => { alive = false; };
  }, []);

  // Clear the "Copied" flash when it times out or the user switches chats.
  useEffect(() => {
    if (copiedIndex === null) return;
    const timer = setTimeout(() => setCopiedIndex(null), 1600);
    return () => clearTimeout(timer);
  }, [copiedIndex]);
  useEffect(() => { setCopiedIndex(null); }, [activeConvId]);

  // Load the active conversation's attached file (bytes from IndexedDB) when
  // switching chats, so an uploaded file persists across turns and reloads.
  // Skips reload when we already hold this conversation's file (e.g. just
  // picked or created it), avoiding a race that would wipe a pending file.
  useEffect(() => {
    let cancelled = false;
    if (!activeConvId) { setAttachment(null); loadedFileConvRef.current = null; return; }
    if (loadedFileConvRef.current === activeConvId) return;
    (async () => {
      const rec = await getFile(activeConvId).catch(() => null);
      if (cancelled) return;
      loadedFileConvRef.current = activeConvId;
      setAttachment(rec || null);
    })();
    return () => { cancelled = true; };
  }, [activeConvId]);

  const activeConv = conversations.find(c => c.id === activeConvId);
  const messages = activeConv?.messages || [];
  /*
   * Entitlement view. `metered` is false when the server isn't counting (auth
   * off, or WordPress unreachable) — then no counter is shown at all rather
   * than a misleading zero. The wall goes up on an explicit server refusal, or
   * when the server's own remaining count has reached zero. Unlike the old
   * client counter this applies to follow-ups too: the server checks every turn.
   */
  const features = ent?.features || OPEN_FEATURES;
  const metered = ent?.metered === true;
  const questionsLeft = metered ? ent.remaining.questions : null;
  // A redeemed code puts the member on Pro for 30 days; the server decides
  // this, and says when it ends.
  const trial = ent && ent.trial && ent.trial.active ? ent.trial : null;
  /*
   * A trial shows BOTH figures: when it ends and how much of the Pro allowance
   * is left. Showing only the date hid the count from exactly the people who
   * have a large one to spend.
   *
   * The date is short (12 Oct, not 12 October 2026) so the pair fits one line
   * at 390px, and carries the year only when the trial runs into another one,
   * where "12 Jan" would otherwise be ambiguous.
   */
  const trialEndsDate = trial && trial.expires_at ? new Date(trial.expires_at) : null;
  const trialEnds = trialEndsDate
    ? new Intl.DateTimeFormat(uiLang, {
      day: 'numeric',
      month: 'short',
      ...(trialEndsDate.getFullYear() !== new Date().getFullYear() ? { year: 'numeric' } : {}),
    }).format(trialEndsDate)
    : '';
  /*
   * What to call this account in the sidebar. The token carries the membership
   * ("Navigator"), which is wrong the moment a trial code lifts the account to
   * Pro: the composer would say "Pro trial until 12 October" while the footer
   * still said Navigator. The server's entitlements already account for the
   * trial, so they win, and a trial is named as one rather than passed off as
   * a paid plan.
   */
  const planLabel = ent && ent.tier_label
    ? (trial ? t('sidebar.planTrial', { plan: ent.tier_label }) : ent.tier_label)
    : null;
  const uploadTurnsLeft = metered ? ent.remaining.upload_turns : null;
  const showUploadTurns = metered && ent.limits.upload_turns > 0;
  const questionLimit = ent?.limits?.questions ?? 0;
  const outOfQuestions = metered && questionsLeft <= 0;
  const atWall = Boolean(blocked) || outOfQuestions;
  const canUpload = features.file_upload !== false;
  // Auth (dormant unless the server reports it enabled): landing when signed
  // out, an account row + logout when signed in.
  // A different member on this device gets their own remembered language.
  const authSub = auth.user && auth.user.sub != null ? String(auth.user.sub) : '';
  useEffect(() => {
    if (!authResolved) return;
    rememberLanguageAccount(auth.user);
    if (pendingLangRef.current) {
      // Chosen before we knew who this was: it is the member's latest intent.
      saveAnswerLanguage(auth.user, pendingLangRef.current);
      pendingLangRef.current = null;
      return;
    }
    setAnswerLang(loadAnswerLanguage(auth.user));
  }, [authResolved, authSub]); // eslint-disable-line react-hooks/exhaustive-deps
  const chooseAnswerLang = (code) => {
    setAnswerLang(code);
    if (authResolved) saveAnswerLanguage(auth.user, code);
    else pendingLangRef.current = code;
    setShowLangSheet(false);
    setTimeout(() => langButtonRef.current?.focus(), 0);
  };
  const closeLangSheet = () => {
    setShowLangSheet(false);
    setTimeout(() => langButtonRef.current?.focus(), 0);
  };
  const showLanding = auth.enabled && !auth.authenticated;
  const showAccount = auth.enabled && auth.authenticated && auth.user;
  const activeProject = projects.find(p => p.id === activeProjectId);
  const editingProject = projects.find(p => p.id === editingProjectId);

  // Stick-to-bottom, ChatGPT/Claude-style. The window is the scroller (topbar
  // and sidebar are sticky). While pinned we follow the newest text; an upward
  // scroll unpins and we leave the user where they are; returning to the bottom
  // re-pins. pinnedRef starts true but the follow effect skips the first render,
  // so a restored conversation opens at the TOP (requirement kept from 3feaab0).
  const pinnedRef = useRef(true);
  /*
   * Whether there is a conversation on screen to follow. Pinned starts true,
   * so without this the follow also ran on the empty home screen and the
   * calculator: on a phone the keyboard opening (a window resize on Android,
   * a visual-viewport change on iOS) and the URL bar collapsing scrolled the
   * page to its bottom, which carried the hero composer up behind the toolbar
   * while the member was typing into it. Only a chat with turns has a
   * "latest" to land on; everything else is left where the browser puts it.
   */
  const followableRef = useRef(false);
  followableRef.current = activeView === 'chat' && messages.length > 0;
  /*
   * Which conversation each scroll concern has already handled. Both start at a
   * sentinel that can never equal a real id (or the null of the empty state),
   * so the very first render counts as "not yet positioned".
   */
  const positionedConvRef = useRef(NO_CONVERSATION);
  const followedConvRef = useRef(NO_CONVERSATION);
  // "Scroll to latest" pill: shown only while the user has scrolled up AND
  // there is newer content below them.
  const [showJump, setShowJump] = useState(false);

  // Open at the top of the content: iOS Safari restores the previous scroll
  // position on reload/back; pin restoration to manual and start at the top so
  // the input is not clipped under the sticky topbar.
  useEffect(() => {
    try { window.history.scrollRestoration = 'manual'; } catch { /* older browsers */ }
    window.scrollTo(0, 0);
  }, []);

  /*
   * Place a newly-opened conversation at its starting position INSTANTLY.
   *
   * useLayoutEffect, not useEffect: this runs before the browser paints, so the
   * view is already where it belongs on the first frame — no animation and no
   * visible travel. Doing it in a passive effect meant the conversation painted
   * at one offset and then slid to another, which is the jump being fixed here.
   *
   * Runs on initial load and on every conversation switch. Smooth scrolling is
   * reserved for changes to an already-visible conversation.
   */
  useLayoutEffect(() => {
    if (positionedConvRef.current === activeConvId) return;
    positionedConvRef.current = activeConvId;
    // Starting position is the TOP of the conversation (kept from 3feaab0).
    window.scrollTo(0, 0);
    pinnedRef.current = true;
    setShowJump(false);
  }, [activeConvId]);

  /*
   * Pin state, taken from the user's GESTURES, never from scroll deltas.
   *
   * The previous version unpinned whenever scrollY dropped a couple of pixels
   * between scroll events, and the browser produces exactly that with no user
   * involvement: a streaming re-render transiently shrinks the document and
   * scrollY gets clamped down; scroll anchoring adjusts around a follow
   * animation. Every such adjustment read as "the user scrolled up", so long
   * streamed answers unpinned themselves and showed the jump pill over a view
   * the user never left. A wheel tick, a touch drag or a navigation key is
   * something the browser cannot emit on its own, so intent comes from those;
   * the scroll position is used only to RE-pin near the bottom and to drive
   * the pill.
   */
  useEffect(() => {
    let frame = 0;
    let pending = false;
    let touchStartY = null;
    // The user's latest scroll gesture: 'up', 'down' or null. Only a gesture
    // stops the follow; only reaching the bottom, or heading back down to it,
    // resumes it (shouldRepin).
    let lastGesture = null;
    /*
     * Gestures that scroll something OTHER than the conversation: the
     * composer's own text once it passes six lines, the sidebar list, a modal.
     * Dragging inside those must not unpin the chat behind them.
     */
    const elsewhere = (e) => !!(e.target && e.target.closest
      && e.target.closest('.footer-input, .sidebar, .modal-backdrop'));

    const refreshJump = () => {
      if (pending) return;
      pending = true;
      frame = requestAnimationFrame(() => {
        pending = false;
        setShowJump(showJumpToLatest({
          pinned: pinnedRef.current, scrollY: window.scrollY,
          scrollHeight: document.documentElement.scrollHeight,
          innerHeight: window.innerHeight,
        }));
      });
    };

    const unpin = () => {
      lastGesture = 'up';
      pinnedRef.current = false;
      refreshJump();
    };

    const onWheel = (e) => {
      if (elsewhere(e)) return;
      if (wheelUnpins(e.deltaY)) unpin();
      else if (wheelHeadsDown(e.deltaY)) lastGesture = 'down';
    };
    const onTouchStart = (e) => {
      touchStartY = elsewhere(e) ? null : (e.touches?.[0]?.clientY ?? null);
    };
    const onTouchMove = (e) => {
      const y = e.touches?.[0]?.clientY;
      if (touchStartY == null || y == null) return;
      if (touchUnpins(touchStartY, y)) unpin();
      else if (touchHeadsDown(touchStartY, y)) lastGesture = 'down';
    };
    const onKeyDown = (e) => {
      // ArrowUp in the composer moves a caret, not the page.
      const el = e.target;
      const typing = el && (el.tagName === 'TEXTAREA' || el.tagName === 'INPUT' || el.isContentEditable);
      if (typing) return;
      if (keyUnpins(e.key)) unpin();
      else if (keyHeadsDown(e.key)) lastGesture = 'down';
    };
    const onScroll = () => {
      // Position only ever re-engages the follow; it can never stop it.
      if (!pinnedRef.current && shouldRepin({ fromBottom: distanceFromBottom(), lastGesture })) {
        pinnedRef.current = true;
        lastGesture = null;
      }
      refreshJump();
    };

    window.addEventListener('wheel', onWheel, { passive: true });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  /*
   * Keyboard tracking via the VisualViewport API.
   *
   * iOS Safari does not resize the layout viewport when the keyboard opens, so
   * window.innerHeight is useless here — anything positioned from it ends up
   * under the keyboard. Android Chrome does resize it. visualViewport reports
   * the genuinely-visible rectangle on both, so the inset is published as a CSS
   * variable and the composer is positioned from that alone.
   *
   * The measurement is rAF-throttled because iOS emits a burst of resize events
   * as the keyboard animates in.
   */
  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!vv) return; // no support: composer stays at bottom: 0, which is correct
    const root = document.documentElement;
    let frame = 0;
    let pending = false;
    // The first measurement only publishes the inset — it must NOT scroll.
    // Doing so on mount dragged a freshly-opened conversation to the bottom
    // after paint, which was half of the visible load-time jump.
    let firstRun = true;

    /*
     * iOS has a keyboard-down button on the keyboard bar that hides the
     * keyboard WITHOUT blurring the field. No blur, and on iOS not always a
     * visualViewport event either: the app was left believing a keyboard was
     * still there, holding --kb-inset at its height. The layout stayed shifted
     * for a keyboard that had gone, and every control was drawn somewhere
     * other than where it could be tapped — the whole app read as dead while
     * typing still worked, because the field still had focus.
     *
     * So the geometry is re-read on a short interval for as long as we believe
     * the keyboard is up, not only when an event arrives. The moment the
     * visible viewport is full height again the inset falls below the
     * threshold and is published as 0, which unsticks the layout. The interval
     * stops itself as soon as that happens, so nothing polls at rest.
     */
    let poll = 0;
    const stopPoll = () => { if (poll) { clearInterval(poll); poll = 0; } };
    const startPoll = () => {
      if (poll) return;
      poll = setInterval(() => {
        apply();
        if (root.dataset.kb !== '1') stopPoll();
      }, 250);
    };

    // Only act when the number actually moves: publishing identical values
    // every 250ms would recalculate style for nothing, and re-scrolling on
    // each tick would fight the reader.
    let lastInset = -1;

    const apply = () => {
      const inset = keyboardInset({
        layoutHeight: window.innerHeight,
        viewportHeight: vv.height,
        offsetTop: vv.offsetTop,
      });
      const open = inset > 0;
      const changed = inset !== lastInset;
      lastInset = inset;
      if (open) startPoll(); else stopPoll();
      if (!changed && !firstRun) return;
      root.style.setProperty('--kb-inset', inset + 'px');
      // The toolbar rides the TOP of the visible rectangle for the same reason
      // the composer rides its bottom: iOS scrolls the visual viewport inside
      // the layout viewport when the keyboard is up, which would otherwise
      // carry a top:0 toolbar out of sight. Zero whenever the keyboard is down.
      root.style.setProperty('--vv-top', (open ? Math.max(0, Math.round(vv.offsetTop || 0)) : 0) + 'px');
      // Drives the tighter gap between composer and keyboard; the resting state
      // keeps the safe-area padding instead.
      root.dataset.kb = open ? '1' : '0';
      // Opening the keyboard shrinks the visible area — if the user was
      // following the answer, keep them at the newest text. Skipped on the
      // initial measurement, which is only establishing the resting inset.
      if (firstRun) { firstRun = false; return; }
      // An open sheet owns the keyboard; the chat behind it stays put.
      if (root.dataset.sheet === '1') return;
      if (pinnedRef.current && followableRef.current) scrollToLatest('auto');
    };

    const onChange = () => {
      if (pending) return;
      pending = true;
      frame = requestAnimationFrame(() => { pending = false; apply(); });
    };

    apply();
    vv.addEventListener('resize', onChange);
    vv.addEventListener('scroll', onChange);
    /*
     * The same reading, from the other directions a keyboard can close:
     * Android resizes the window, a rotation changes everything, and coming
     * back to a restored page can arrive with stale geometry.
     */
    window.addEventListener('resize', onChange);
    window.addEventListener('orientationchange', onChange);
    window.addEventListener('pageshow', onChange);
    document.addEventListener('visibilitychange', onChange);
    return () => {
      vv.removeEventListener('resize', onChange);
      vv.removeEventListener('scroll', onChange);
      window.removeEventListener('resize', onChange);
      window.removeEventListener('orientationchange', onChange);
      window.removeEventListener('pageshow', onChange);
      document.removeEventListener('visibilitychange', onChange);
      stopPoll();
      if (frame) cancelAnimationFrame(frame);
      root.style.removeProperty('--kb-inset');
      root.style.removeProperty('--vv-top');
      delete root.dataset.kb;
    };
  }, []);

  /*
   * Publish the composer's real height so the conversation can reserve exactly
   * that much space beneath it. It changes as the textarea grows, the usage
   * counter appears, or a file is attached — a hard-coded value would either
   * clip the last message or leave a gap.
   */
  useLayoutEffect(() => {
    const el = footerRef.current;
    const root = document.documentElement;
    // No fixed composer (empty state, calculator): reserve nothing.
    if (!el) { root.style.setProperty('--footer-h', '0px'); return; }
    /*
     * Measure synchronously BEFORE the first paint. The ResizeObserver below
     * only fires after paint, so relying on it alone meant the first frame used
     * the CSS default and the conversation then jumped as the real height
     * landed — a visible layout shift on load. The CSS defaults are now close
     * to the real heights too, so even the pre-JS frame is about right.
     */
    const measure = () => {
      const h = Math.round(el.offsetHeight || 0);
      if (h > 0) root.style.setProperty('--footer-h', h + 'px');
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return; // jsdom / older browsers
    const ro = new ResizeObserver(() => {
      // offsetHeight, NOT entry.contentRect.height — contentRect is the CONTENT
      // box and excludes the composer's own vertical padding, which would
      // under-report the height and let the jump pill overlap the composer.
      const h = Math.round(el.offsetHeight || 0);
      if (h <= 0) return;
      /*
       * Kept in sync with the composer: when the textarea grows a line (or
       * shrinks back after send) the reserved padding changes by the same
       * amount. A reader who was AT the bottom stays at the bottom, so the
       * last line is never left under the taller composer. Read before the
       * padding moves, act after.
       */
      const wasAtBottom = distanceFromBottom() <= 8;
      root.style.setProperty('--footer-h', h + 'px');
      if (wasAtBottom || (pinnedRef.current && streamingRef.current)) scrollToLatest('auto');
    });
    // border-box: opening the keyboard swaps the composer's bottom padding
    // (safe area for the tuned gap), which changes its height without
    // changing its content box. A content-box observer missed that by 2px.
    ro.observe(el, { box: 'border-box' });
    return () => { ro.disconnect(); root.style.setProperty('--footer-h', '0px'); };
  }, [messages.length > 0, atWall]);

  /*
   * The top edge, by the same mechanism as the bottom: the chat toolbar's real
   * height is published as --topbar-h and the conversation reserves exactly
   * that much padding above its first line. It changes when the project
   * context wraps or the theme changes the toolbar's padding. Safari has no
   * scroll anchoring, so a reader in the middle of the conversation is moved
   * by the same delta and the text under their eyes does not shift.
   */
  useLayoutEffect(() => {
    const el = topbarRef.current;
    const root = document.documentElement;
    if (!el) return;
    let last = Math.round(el.offsetHeight || 0);
    if (last > 0) root.style.setProperty('--topbar-h', last + 'px');
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      const h = Math.round(el.offsetHeight || 0);
      if (h <= 0 || h === last) return;
      const delta = h - last;
      const wasAtBottom = distanceFromBottom() <= 8;
      last = h;
      root.style.setProperty('--topbar-h', h + 'px');
      if (wasAtBottom) scrollToLatest('auto');
      else if (window.scrollY > 0) window.scrollBy(0, delta);
    });
    ro.observe(el, { box: 'border-box' });
    return () => ro.disconnect();
  }, [activeView]);

  /*
   * Preserve reading position across window resize and device rotation by
   * holding DISTANCE FROM THE BOTTOM rather than the absolute offset — in a
   * bottom-anchored chat, changing the viewport height would otherwise shift
   * the conversation under the reader.
   */
  useEffect(() => {
    let frame = 0;
    const onResize = () => {
      const before = document.documentElement.scrollHeight - window.innerHeight - window.scrollY;
      if (frame) cancelAnimationFrame(frame);
      // Measure after the browser has settled on the new layout.
      frame = requestAnimationFrame(() => {
        frame = 0;
        if (document.documentElement.dataset.sheet === '1') return;
        // No conversation: neither the follow nor distance-from-bottom applies;
        // the browser keeps the focused field in view on its own.
        if (!followableRef.current) return;
        if (pinnedRef.current) {
          scrollToLatest('auto');
          return;
        }
        window.scrollTo(0, preservedScrollTop({
          distanceFromBottom: before,
          scrollHeight: document.documentElement.scrollHeight,
          innerHeight: window.innerHeight,
        }));
      });
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  /*
   * Enter sends, Shift+Enter inserts a newline. Needs a textarea (an <input>
   * cannot hold a newline at all), and preventDefault stops the newline being
   * inserted on the send path. IME composition is excluded — while composing
   * Japanese/Chinese text, Enter commits the candidate and must not send.
   */
  const onComposerKeyDown = (e) => {
    if (!enterSends({ key: e.key, shiftKey: e.shiftKey, isComposing: !!e.nativeEvent?.isComposing, touch: isTouchComposer() })) return;
    e.preventDefault();
    ask(query);
  };
  // What the on-screen keyboard's return key says: a newline key on touch,
  // Send on desktop (where Enter does send).
  const composerEnterHint = isTouchComposer() ? 'enter' : 'send';

  /*
   * Auto-grow the composer with its content, up to a cap (CSS max-height then
   * scrolls). Reset to 'auto' first so the textarea can also shrink back down.
   */
  useEffect(() => {
    const el = inputRef.current;
    if (!el || el.tagName !== 'TEXTAREA') return;
    // Empty rests at ONE line whatever the placeholder says: a long
    // placeholder wraps on a phone and would otherwise count as text.
    if (!el.value) { el.style.height = ''; return; }
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  }, [query]);

  // Re-pin and return to the newest message. Used by the jump button.
  const jumpToLatest = () => {
    pinnedRef.current = true;
    setShowJump(false);
    scrollToLatest('smooth');
  };

  /*
   * Follow the newest content while pinned. streamingLen grows with each
   * streamed chunk, so this fires per chunk; messages.length covers new turns.
   *
   * SMOOTH SCROLLING IS ONLY FOR CHANGES TO AN ALREADY-VISIBLE CONVERSATION.
   * The first render of a conversation — initial load, or opening/restoring one
   * from the sidebar — is positioned instantly by the layout effect above, and
   * that render is skipped here so nothing visibly travels. Guarding on the
   * conversation id rather than a mount-only flag is what makes switching
   * conversations instant too; a mount-only guard let every subsequent open
   * animate.
   */
  const streamingLen = busyIndex != null ? (messages[busyIndex]?.answer?.length || 0) : 0;

  /*
   * A NEW TURN follows smoothly: sending a question is a single deliberate
   * movement, the same class of action as the jump pill, and it should read as
   * one. This effect deliberately does not depend on streamingLen.
   */
  useEffect(() => {
    if (followedConvRef.current !== activeConvId) {
      followedConvRef.current = activeConvId;
      return; // freshly opened: already positioned, without animation
    }
    if (!pinnedRef.current) return;
    scrollToLatest('smooth');
  }, [messages.length, activeConvId]);

  /*
   * THE STREAM follows instantly. A smooth animation restarted by every chunk
   * chases a bottom it never reaches on a fast stream, and its in-flight
   * adjustments are what used to get misread as user scrolling. Gluing the
   * view to the end on each painted frame is what ChatGPT-style follow
   * actually is; with appends batched to one per frame this runs at frame
   * cadence, not chunk cadence.
   *
   * This effect only sees flushes that get their own commit. A burst answer
   * that arrives in the same commit as the stream ending is invisible here
   * (streamingLen reads 0 on both sides of it), which stranded the view a
   * full page above the answer on live; the imperative completion glue at the
   * end of streamAnswer covers that case. The per-conversation reset keeps a
   * switch from gluing a freshly opened conversation to the bottom, which
   * must open at the top.
   */
  const prevStreamRef = useRef({ convId: NO_CONVERSATION, len: 0 });
  /*
   * useLayoutEffect, not useEffect: the glue lands BEFORE the browser paints
   * the frame that grew the answer. A passive effect runs after paint, so the
   * new line was drawn once under the composer and then jumped up.
   */
  useLayoutEffect(() => {
    const prev = prevStreamRef.current;
    const prevLen = prev.convId === activeConvId ? prev.len : 0;
    prevStreamRef.current = { convId: activeConvId, len: streamingLen };
    if (streamingLen === prevLen) return;                 // nothing streamed
    if (followedConvRef.current !== activeConvId) return; // not yet on screen
    if (!pinnedRef.current) return;
    scrollToLatest('auto');
  }, [streamingLen, activeConvId]);

  /*
   * Growth the stream effect cannot see: the answer's action row and sourcing
   * link appearing as it completes, an image or a table finishing layout, a
   * font swap. While an answer is in flight and the reader is pinned, any
   * change in the conversation's height re-glues. Limited to in-flight turns
   * so a restored conversation, which opens at the TOP, is never dragged down
   * by its own images loading.
   */
  const streamingRef = useRef(false);
  streamingRef.current = busyIndex != null;
  const contentRef = useRef(null);
  useEffect(() => {
    const el = contentRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      if (streamingRef.current && pinnedRef.current && followedConvRef.current === activeConvId) scrollToLatest('auto');
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [activeConvId, activeView]);

  /*
   * Starter prompts for the empty state.
   *
   * Chosen against what the corpus actually holds, because a weak first answer
   * is worse than no prompt at all. Pumps, UV and oxygen supply are the three
   * best-covered equipment classes in the manuals; each of these carries the
   * numbers Gia needs to derive a real specification rather than ask a
   * clarifying question back.
   *
   * The first is deliberately phrased as an improvement, not a crisis. "DO is
   * crashing" would be classified as an emergency by the answer extraction,
   * which correctly suppresses the sourcing surface, so a starter prompt worded
   * that way would lead nowhere.
   *
   * These go through ask() exactly as typed input does. Nothing here knows
   * about sourcing, extraction or emergencies; it is a string and a click.
   */
  // The English originals live in starterStrings.js with fixed translations
  // shown when an explicit answer language is chosen; Auto shows English.
  const starter = starterStringsFor(answerLang);
  const starterLang = starterLangAttr(answerLang);
  const examples = starter.examples;
  // The English originals, which document search uses for a translated chip.
  const englishExamples = starterStringsFor('en').examples;

  const newProject = () => {
    const name = window.prompt(t('project.namePrompt'));
    if (!name?.trim()) return;
    const proj = {
      id: 'proj_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7),
      name: name.trim(),
      systemProfile: null,
      createdAt: Date.now()
    };
    setProjects(prev => [proj, ...prev]);
    setActiveProjectId(proj.id);
    setExpandedProjects(prev => new Set([...prev, proj.id]));
    setActiveConvId(null);
    setEditingProjectId(proj.id);
  };

  const renameProject = (id) => {
    const current = projects.find(p => p.id === id);
    const name = window.prompt(t('project.renamePrompt'), current?.name);
    if (!name?.trim()) return;
    setProjects(prev => prev.map(p => p.id === id ? { ...p, name: name.trim() } : p));
  };

  const deleteProject = (id) => {
    const current = projects.find(p => p.id === id);
    const count = conversations.filter(c => c.projectId === id).length;
    const msg = count > 0
      ? t('project.deleteWithChats', { name: current.name, count })
      : t('project.delete', { name: current.name });
    if (!window.confirm(msg)) return;
    const doomed = conversations.filter(c => c.projectId === id);
    setProjects(prev => prev.filter(p => p.id !== id));
    setConversations(prev => prev.filter(c => c.projectId !== id));
    removeConvsFromStore(doomed.map(c => c.id));
    doomed.forEach(c => deleteFile(c.id).catch(() => {}));
    if (activeProjectId === id) setActiveProjectId(null);
    if (doomed.some(c => c.id === activeConvId)) setActiveConvId(null);
  };

  const saveProfile = (id, profile) => {
    setProjects(prev => prev.map(p => p.id === id ? { ...p, systemProfile: profile } : p));
  };

  const toggleProject = (id) => {
    setExpandedProjects(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectProject = (id) => {
    setActiveProjectId(id);
    setActiveConvId(null);
    setActiveView('chat');
    setExpandedProjects(prev => new Set([...prev, id]));
    setSidebarOpen(false);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  // Top-level "+ New chat" — always general (not in any project)
  const newChat = () => {
    setActiveConvId(null);
    setActiveProjectId(null);
    setActiveView('chat');
    setSidebarOpen(false);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  // Topbar "new chat": clear the current conversation and detach the active
  // file, returning to the empty state. No-op when already at a fresh empty
  // state (no active conversation and nothing attached), so it can't clobber.
  /*
   * Redeem a trial code. The server answers the same way for an unknown, a
   * spent and an expired code, so this cannot tell the member which it was
   * either. A success brings fresh entitlements back with it, so the counter
   * line switches to the trial without a second request.
   */
  const redeemCode = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const code = codeValue.trim();
    if (!code || codeState === 'checking') return;
    setCodeState('checking');
    try {
      const r = await fetch('/api/trial/redeem', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      const d = await r.json().catch(() => null);
      if (d && d.valid) {
        if (d.entitlements && d.entitlements.tier) setEnt(d.entitlements);
        setCodeState('done');
        setCodeValue('');
        setCodeOpen(false);
      } else {
        setCodeState('invalid');
      }
    } catch {
      setCodeState('error');
    }
  };

  /*
   * "Hide sidebar" means two different things.
   *
   * On a phone the sidebar is a drawer over the page, so hiding it is closing
   * the drawer — and only that. Setting the desktop preference here as well
   * would silently collapse the sidebar on the member's laptop, which is not
   * what tapping an X in a drawer asks for.
   *
   * The drawer breakpoint is read from the same media query the stylesheet
   * uses, so the two cannot drift apart.
   */
  const DRAWER_QUERY = '(max-width: 768px), (pointer: coarse) and (max-height: 440px)';
  const hideSidebar = () => {
    const drawer = typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia(DRAWER_QUERY).matches
      : false;
    setSidebarOpen(false);
    if (!drawer) setSidebarHidden(true);
  };

  const startNewChat = () => {
    // On a phone this is reached from inside the drawer, so close it first:
    // the point of the action is to land on an empty chat ready to type.
    setSidebarOpen(false);
    /*
     * Already on an empty chat? Then there is nothing to start, and the useful
     * thing is the cursor. Returning silently, as this used to, made the logo
     * and the pen feel broken on exactly the screen where they are most
     * likely to be pressed.
     */
    if (!activeConvId && !attachment) {
      setTimeout(() => inputRef.current?.focus(), 50);
      return;
    }
    setActiveConvId(null);
    setActiveProjectId(null);
    setActiveView('chat');
    setSidebarOpen(false);
    setAttachment(null);          // detach the in-memory file view (does not delete
    loadedFileConvRef.current = null; // the old conversation's stored file)
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  /*
   * Sign out, in the order that makes each step survive the next one failing.
   *
   *   1. tell this host to drop the session cookie, and to remember that a
   *      sign-out happened so its entry guard stops handing strangers to the
   *      mint. It answers with where to go next.
   *   2. clear what the member chose to clear (see clearSignOutData and
   *      clearSavedWork).
   *   3. LEAVE, to the WordPress sign-out route, which ends the session that
   *      actually owns the member's identity and lands them on /iq/.
   *
   * Step 3 is the one that used to be missing, and without it the other two
   * are undone within a second: the app reloaded, the entry guard found no
   * session, WordPress still knew the member and minted a fresh token. That is
   * why this navigates away instead of reloading.
   *
   * The fetch is capped, because a hanging request must not leave someone
   * staring at a button that says it is signing them out. If it never answers
   * we carry on: WordPress still gets ended, which breaks the loop, and the
   * stale cookie here dies with its own expiry.
   *
   * Nothing here touches usage counts. Questions and upload-turns are metered
   * server-side against the WordPress account, so signing out neither resets
   * an allowance nor costs the member one.
   */
  const [signOutOpen, setSignOutOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const logout = async (clearSaved) => {
    if (signingOut) return;
    setSigningOut(true);

    let next = SIGNOUT_URL;
    try {
      const abort = new AbortController();
      const timer = setTimeout(() => abort.abort(), 6000);
      const res = await fetch('/auth/logout', { method: 'POST', signal: abort.signal });
      clearTimeout(timer);
      const body = await res.json().catch(() => null);
      if (body && typeof body.next === 'string' && body.next) next = body.next;
    } catch { /* offline, blocked or too slow: the walk-away below still runs */ }

    clearSignOutData();
    if (clearSaved) clearSavedWork();
    try { await clearAllFiles(); } catch { /* never blocks the sign-out */ }

    // replace(), not assign(): Back must not return to a signed-in-looking app
    // built from state this tab still holds in memory.
    try { window.location.replace(next); } catch { window.location.href = next; }
  };

  // Project-level "+ New chat in this project"
  const newChatInProject = (projectId) => {
    setActiveProjectId(projectId);
    setActiveConvId(null);
    setActiveView('chat');
    setExpandedProjects(prev => new Set([...prev, projectId]));
    setSidebarOpen(false);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const selectChat = (id) => {
    setActiveConvId(id);
    const conv = conversations.find(c => c.id === id);
    setActiveProjectId(conv?.projectId || null);
    setActiveView('chat');
    setSidebarOpen(false);
    pinnedRef.current = true; // opening a chat shows its latest turn
  };

  const deleteChat = (id, e) => {
    e.stopPropagation();
    setConversations(prev => prev.filter(c => c.id !== id));
    removeConvsFromStore([id]);
    deleteFile(id).catch(() => {});
    if (activeConvId === id) setActiveConvId(null);
  };

  // Ratings are stored per conversation + message index. Clicking the active
  // rating again clears it.
  const setRating = (msgIndex, value) => {
    if (!activeConvId) return;
    setRatings(prev => {
      const forConv = { ...(prev[activeConvId] || {}) };
      if (value === null || forConv[msgIndex] === value) delete forConv[msgIndex];
      else forConv[msgIndex] = value;
      const next = { ...prev };
      if (Object.keys(forConv).length) next[activeConvId] = forConv;
      else delete next[activeConvId];
      return next;
    });
  };

  /*
   * Copy an answer as both rich text and plain text.
   *
   * text/html comes from the rendered node rather than being re-generated from
   * the markdown, so what lands in Word, Gmail or Docs is exactly what the user
   * is looking at. text/plain is the markdown with its syntax stripped, so a
   * plain field gets prose instead of ## and **.
   *
   * Browsers that lack ClipboardItem (or refuse the write) fall back to the
   * previous single-format path. That fallback now sends the stripped text
   * rather than raw markdown — pasting ## into a plain field is the bug being
   * fixed here, and it is worth fixing on the fallback path too.
   */
  const copyAnswer = async (msgIndex, markdown) => {
    const plain = markdownToPlainText(markdown);

    // Read the rendered output, minus the streaming loader if one is present.
    let html = null;
    const node = answerRefs.current[msgIndex];
    if (node) {
      const clone = node.cloneNode(true);
      clone.querySelectorAll('.fish-loader').forEach((el) => el.remove());
      html = clone.innerHTML;
    }

    const canWriteRich =
      typeof window !== 'undefined' &&
      typeof window.ClipboardItem === 'function' &&
      navigator.clipboard &&
      typeof navigator.clipboard.write === 'function';

    if (canWriteRich && html) {
      try {
        // Built synchronously and written immediately: Safari drops the
        // clipboard permission if an await intervenes before the write.
        await navigator.clipboard.write([
          new window.ClipboardItem({
            // The charset meta keeps Word from mangling °C, ³ and ₂.
            'text/html': new Blob([`<meta charset="utf-8">${html}`], { type: 'text/html' }),
            'text/plain': new Blob([plain], { type: 'text/plain' }),
          }),
        ]);
        setCopiedIndex(msgIndex);
        return;
      } catch {
        // Permission denied, unsupported MIME type, non-secure context — fall
        // through to the plain-text path rather than failing the copy.
      }
    }

    try {
      await navigator.clipboard.writeText(plain);
    } catch {
      // Clipboard API needs a secure context; fall back to a temp textarea.
      const ta = document.createElement('textarea');
      ta.value = plain;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch {}
      document.body.removeChild(ta);
    }
    setCopiedIndex(msgIndex);
  };

  const editSavedCalc = (entry) => {
    // Guarded in the UI too; entries saved before rawInputs existed carry only
    // display labels, which no longer map to field keys.
    if (!entry || !entry.rawInputs) return;
    setPendingEdit({
      id: entry.id,
      ts: entry.ts,
      calcId: entry.calcId,
      rawInputs: entry.rawInputs,
      calcLabel: entry.calcLabel,
    });
    setActiveView('calc');
    setSidebarOpen(false);
  };

  const updateConv = (convId, updater) => {
    setConversations(prev => prev.map(c => c.id === convId ? updater(c) : c));
  };

  /*
   * Tier gate for uploading. Returns true when allowed; otherwise raises the
   * upgrade prompt and returns false. Called from every entry point (button,
   * drag-and-drop) so a blocked tier gets the same clear explanation rather
   * than a silent no-op or a 402 after the fact. The server enforces this
   * independently — this is the affordance, not the control.
   */
  const allowUpload = () => {
    if (canUpload) return true;
    setFeaturePrompt({
      title: t('file.proTitle'),
      message: t('file.proMessage'),
    });
    return false;
  };

  // Single file-handling path shared by the upload button, drag-and-drop and
  // paste: same validation, same fileStore persistence, same active-file
  // binding.
  const handlePickedFile = (file) => {
    if (!file) return;
    if (!allowUpload()) return;
    const mediaType = uploadMediaType(file);
    if (!mediaType) { alert(t('file.badType')); return; }
    if (file.size > 20 * 1024 * 1024) { alert(t('file.tooLarge')); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const res = String(reader.result || '');
      const base64 = res.split(',')[1] || '';
      const rec = { name: file.name, mediaType, data: base64 };
      setAttachment(rec);
      // If a conversation is already open, bind the file to it now. For a
      // brand-new chat (no id yet) it's persisted on first send, in ask().
      if (activeConvId) {
        loadedFileConvRef.current = activeConvId;
        putFile(activeConvId, rec).catch(() => {});
        updateConv(activeConvId, c => ({ ...c, activeFile: { name: rec.name, mediaType: rec.mediaType } }));
      }
    };
    reader.readAsDataURL(file);
  };

  const onPickFile = (e) => {
    handlePickedFile(e.target.files && e.target.files[0]);
    e.target.value = ''; // reset so re-picking the same file still fires onChange
  };

  /*
   * Paste into the question box: a file on the clipboard attaches exactly as
   * the upload button would (screenshots are the usual case). Plain text
   * pastes as text, untouched.
   *
   * Word, Excel and most other apps put a PICTURE of the selection on the
   * clipboard next to its text. Copied cells or paragraphs must still paste
   * as words, so an image only wins when the clipboard has no other text,
   * or when that text is just the file's own name or path (what a file
   * copied in Finder or Explorer carries). Anything that is not an image is
   * always a real file and always attaches.
   */
  const onComposerPaste = (e) => {
    const cd = e.clipboardData;
    const files = Array.from((cd && cd.files) || []);
    if (!files.length) return;
    const file = files[0];
    const text = ((cd.getData && cd.getData('text/plain')) || '').trim();
    const isImage = /^image\//.test(file.type);
    const textIsFileName = files.some((f) => text === f.name || text.endsWith('/' + f.name) || text.endsWith('\\' + f.name));
    if (isImage && text && !textIsFileName) return;
    e.preventDefault();
    handlePickedFile(file);
  };

  // A drop that misses the drop zone would otherwise make the browser navigate
  // to the file and blow away the conversation. Swallow dragover/drop globally.
  useEffect(() => {
    const prevent = (e) => e.preventDefault();
    window.addEventListener('dragover', prevent);
    window.addEventListener('drop', prevent);
    return () => {
      window.removeEventListener('dragover', prevent);
      window.removeEventListener('drop', prevent);
    };
  }, []);

  // Drag-and-drop over the chat area, routed into the same handlePickedFile
  // path as the upload button. Only in chat view, only for actual files.
  const isFileDrag = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');
  const onAreaDragEnter = (e) => {
    if (activeView !== 'chat' || !isFileDrag(e)) return;
    e.preventDefault();
    dragDepth.current += 1;
    setDragActive(true);
  };
  const onAreaDragOver = (e) => {
    if (activeView !== 'chat' || !isFileDrag(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };
  const onAreaDragLeave = (e) => {
    if (activeView !== 'chat') return;
    dragDepth.current -= 1;
    if (dragDepth.current <= 0) { dragDepth.current = 0; setDragActive(false); }
  };
  const onAreaDrop = (e) => {
    if (activeView !== 'chat') return;
    e.preventDefault();
    dragDepth.current = 0;
    setDragActive(false);
    // Multi-file drop: take the first, same as the upload button.
    handlePickedFile(e.dataTransfer?.files && e.dataTransfer.files[0]);
  };

  // Detach the file from the current conversation (stops re-sending it).
  const detachFile = () => {
    setAttachment(null);
    if (activeConvId) {
      loadedFileConvRef.current = activeConvId;
      deleteFile(activeConvId).catch(() => {});
      updateConv(activeConvId, c => ({ ...c, activeFile: null }));
    }
  };

  // Runs one question against the backend and streams the reply into
  // messages[msgIndex] of `convId`. Shared by ask() (new turn, appended) and
  // retryTurn() (existing turn, answer replaced in place).
  /*
   * searchText: what document retrieval is asked, when it should differ from
   * the question. A translated starter prompt sends its ENGLISH original here:
   * the manuals are English, and measured against them the translated prompts
   * lost most of their matches (ไทย found no documents for three of the four).
   * The question itself, and Gia's answer, stay in the chosen language.
   */
  const streamAnswer = async ({ convId, qText, searchText, att, history, msgIndex }) => {
    /*
     * `loading` is state, so it is not true until React re-renders. A double
     * tap on Send, or a held Enter key, can therefore get two turns past the
     * `loading` guards and be charged twice by the server, which counts each
     * answered turn exactly once and has no way to know they were the same
     * question. A ref flips synchronously and closes that window.
     */
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setLoading(true);
    setBusyIndex(msgIndex);
    setLoadPhase('searching');

    const updateAnswer = (text, replace = false) => {
      updateConv(convId, c => {
        const msgs = [...c.messages];
        const target = msgs[msgIndex];
        if (!target) return c;
        msgs[msgIndex] = { ...target, answer: replace ? text : target.answer + text };
        return { ...c, messages: msgs };
      });
    };
    try {
      /*
       * Retrieval is an augmentation, not a precondition. It gets its own
       * try/catch so a transport failure degrades to "no context" like a 500
       * does, instead of falling through to the outer catch and killing a turn
       * that /api/chat could have answered perfectly well.
       */
      let searchRes = null;
      let searchData = null;
      try {
        searchRes = await fetch('/api/search', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ query: searchText || qText })
        });
        searchData = await searchRes.json().catch(() => null);
      } catch (searchError) {
        console.warn('AquaNetica IQ: retrieval request failed —', searchError.message);
      }
      // Retrieval done — the mark darts once while the model call is opened.
      setLoadPhase('found');

      const hits = (searchRes?.ok && Array.isArray(searchData?.results)) ? searchData.results : [];

      let context = '';
      if (hits.length > 0) {
        context = '\n\nRelevant documentation for context (do not cite or reference these in your reply):\n\n';
        hits.forEach((r, i) => {
          const excerpt = r.payload?.text || '';
          context += `${i + 1}. ${r.payload?.filename || ''}: ${excerpt}\n\n`;
        });
      } else {
        /*
         * No context this turn. Tell Gia rather than the user.
         *
         * This used to pass silently: a 500 from /api/search parses fine as
         * JSON, results comes back undefined, and the turn proceeds with no
         * documentation and nothing anywhere to say so. Retrieval could be down
         * for a week and every answer would still look manual-backed.
         *
         * A banner is the wrong fix. It trains people to ignore banners over a
         * blip, and the real problem is not that a request failed, it is that
         * an answer about a specific product may now be coming from general
         * knowledge on an interface that implies otherwise. Stating the fact in
         * the message lets Gia's existing honesty rules do the work: she
         * already may not give model numbers, prices or specifications without
         * a source, and is already told to say plainly when she is unsure.
         *
         * The two cases are worded differently because they are different
         * facts, and after the score threshold lands the second becomes the
         * ordinary one rather than a fault.
         */
        context = searchRes?.ok
          ? '\n\n[No documentation in the AquaNetica library matched this question. Answer from general knowledge.]'
          : '\n\n[The documentation library could not be reached for this question. Answer from general knowledge.]';

        // A breadcrumb for whoever is looking at a browser console, kept
        // distinct from the model-facing note above.
        console.warn(
          searchRes?.ok
            ? 'AquaNetica IQ: retrieval returned no matches; answering without documentation context.'
            : `AquaNetica IQ: retrieval unavailable (${searchRes ? 'HTTP ' + searchRes.status : 'no response'}); answering without documentation context.`
        );
      }

      const projectContext = buildProjectContext(activeProject);
      const fullMessage = qText + projectContext + context;

      const chatRes = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: fullMessage,
          history,
          // The toolbar's answer language; the server re-validates it.
          language: answerLang,
          // The name titles the extracted text of a Word, Excel or PowerPoint file.
          ...(att ? { attachment: { mediaType: att.mediaType, data: att.data, name: att.name } } : {})
        })
      });

      /*
       * Refusals, surfaced as the upgrade wall immediately — no refresh needed.
       *   402  a tier limit was hit, or the turn used a feature this tier lacks.
       *        The body names which limit and its value; we render it verbatim
       *        so the wall can never disagree with what the server enforced.
       *   429  the per-IP daily backstop (not tier-related).
       */
      if (chatRes.status === 402 || chatRes.status === 429) {
        const fallback = chatRes.status === 402
          ? "You've reached a limit on your plan."
          : "You've reached today's message limit. Please try again tomorrow.";
        let payload = { error: chatRes.status === 402 ? 'tier_limit' : 'daily_limit', message: fallback };
        try {
          const j = await chatRes.json();
          if (j && typeof j === 'object') payload = { ...payload, ...j };
        } catch { /* keep the fallback */ }
        updateAnswer(payload.message || fallback, true);
        setBlocked(payload);
        setLoadPhase(null);
        inFlightRef.current = false;
        setLoading(false);
        setBusyIndex(null);
        return;
      }

      /*
       * The file itself was refused: a type the server does not take (415), or
       * a Word, Excel or PowerPoint file it could not read (422). Said as the
       * answer, in the member's language, not as the upgrade wall: nothing was
       * charged and nothing about their plan has changed.
       */
      if (chatRes.status === 415 || chatRes.status === 422) {
        let code = '';
        try { code = (await chatRes.json())?.error || ''; } catch { /* generic below */ }
        updateAnswer(t(FILE_ERROR_KEYS[code] || 'file.unreadable'), true);
        setLoadPhase(null);
        inFlightRef.current = false;
        setLoading(false);
        setBusyIndex(null);
        return;
      }

      if (!chatRes.ok || !chatRes.body) throw new Error('No response body');

      const reader = chatRes.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let streamStarted = false;

      /*
       * Paint cadence. Chunks arrive far faster than frames, and every append
       * used to be its own setState, so the whole answer re-rendered per chunk
       * and the layout churned mid-stream. Appends now accumulate here and
       * flush once per animation frame: same text, same order, one steady
       * paint per frame. The stream itself is untouched; this only decides
       * when the screen looks at it.
       */
      let pendingText = '';
      let flushQueued = false;
      const flushPending = () => {
        flushQueued = false;
        if (!pendingText) return;
        const flushed = pendingText;
        pendingText = '';
        updateAnswer(flushed);
      };
      const queueText = (piece) => {
        pendingText += piece;
        if (!flushQueued) {
          flushQueued = true;
          requestAnimationFrame(flushPending);
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6).trim();
          if (data === '[DONE]') continue;
          try {
            const parsed = JSON.parse(data);
            if (parsed.text) {
              // First token: the loader switches from the one-shot dart to the
              // vertical bob that rides alongside the streaming text.
              if (!streamStarted) { streamStarted = true; setLoadPhase('writing'); }
              queueText(parsed.text);
            }
            if (parsed.error) updateAnswer(t('chat.errSomething', { detail: parsed.error }), true);
            // Fresh remaining counts, sent after the turn is charged. Keeps the
            // counter honest without a refetch; absent when the server didn't
            // meter this turn (auth off, or the store was unreachable).
            if (parsed.usage) {
              setEnt(prev => (prev ? { ...prev, ...parsed.usage, metered: true } : prev));
            }
            // Structured sourcing signal (sent after the answer streams): the
            // model specced concrete equipment and it isn't an emergency. Store
            // it on this turn so the answer can offer a sourcing link.
            if (parsed.sourcing_available && parsed.equipment_specs) {
              const specs = parsed.equipment_specs;
              updateConv(convId, c => {
                const msgs = [...c.messages];
                if (!msgs[msgIndex]) return c;
                msgs[msgIndex] = {
                  ...msgs[msgIndex],
                  sourcing: {
                    item: specs.item,
                    specs: specs.specs,
                    // Reference candidates the answer named; absent on older
                    // stored turns, hence the fallback.
                    models: Array.isArray(specs.recommended_models) ? specs.recommended_models : [],
                  },
                };
                return { ...c, messages: msgs };
              });
            }
          } catch (e) {}
        }
      }
      flushPending();
    } catch (err) {
      console.error(err);
      updateAnswer(t('chat.errNoBackend'), true);
    }
    setLoadPhase(null);
    inFlightRef.current = false;
    setLoading(false);
    setBusyIndex(null);
    /*
     * The completion glue. A tool-using answer thinks for tens of seconds and
     * then arrives in one flush, and that flush lands in the same React commit
     * as the stream ending — so no state the follow effect can watch ever
     * changes while "streaming". This is the one place every stream ends,
     * success and error alike, so the view is landed here, after the commit
     * has painted (two frames: one for React's flush, one for layout). Still
     * gesture-respecting: an unpinned view stays exactly where the user put it.
     */
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (pinnedRef.current) scrollToLatest('auto');
    }));
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const ask = async (q, { searchQuery } = {}) => {
    const att = attachment;
    if ((!q.trim() && !att) || loading) return;
    /*
     * The wall applies to every turn now, follow-ups included — the server
     * checks each one, so continuing an existing conversation past the
     * allowance no longer slips through. This is only the local mirror of that
     * rule; the authoritative refusal is the 402 handled in streamAnswer.
     */
    if (atWall) return;
    pinnedRef.current = true; // sending a question re-pins so its answer follows
    const qText = q.trim() || 'Please review the attached file.';
    setActiveView('chat');
    setQuery('');
    // No client-side counting: the server charges the turn only after it has
    // successfully answered, and reports the new remaining figures back on the
    // stream (the `usage` event above).
    // Note: the file is intentionally NOT cleared — it stays bound to the
    // conversation and is re-sent each turn until the user detaches it.

    // Capture history from turns completed *before* this one.
    const history = buildHistoryMessages(activeConv?.messages || []);

    let convId = activeConvId;
    let msgIndex;
    const newTurn = { question: att ? qText + ' [attached: ' + att.name + ']' : qText, answer: '' };
    // Kept on the turn so Retry searches the same way the original did.
    const searchText = searchQuery && searchQuery.trim() && searchQuery.trim() !== qText ? searchQuery.trim() : undefined;
    if (searchText) newTurn.searchQuery = searchText;

    if (!convId) {
      convId = 'conv_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7);
      const newConv = {
        id: convId,
        projectId: activeProjectId,
        title: qText.length > 50 ? qText.slice(0, 50) + '…' : qText,
        messages: [newTurn],
        activeFile: att ? { name: att.name, mediaType: att.mediaType } : null,
        createdAt: Date.now(),
        lastActive: Date.now()
      };
      setConversations(prev => [newConv, ...prev]);
      setActiveConvId(convId);
      // Bind the file to the freshly created conversation and claim it so the
      // load-on-switch effect won't overwrite it.
      loadedFileConvRef.current = convId;
      if (att) putFile(convId, att).catch(() => {});
      msgIndex = 0;
    } else {
      msgIndex = (activeConv?.messages || []).length;
      // Stamp lastActive each turn so the idle-based GC keeps active chats.
      updateConv(convId, c => ({ ...c, lastActive: Date.now(), messages: [...c.messages, newTurn] }));
    }

    await streamAnswer({ convId, qText, searchText, att, history, msgIndex });
  };

  // Re-ask the question at `msgIndex`, replacing that answer in place. The
  // history sent is only the turns before it, so the retry sees the same
  // context the original did.
  const retryTurn = async (msgIndex) => {
    if (loading || !activeConvId) return;
    const turn = messages[msgIndex];
    if (!turn) return;
    const qText = String(turn.question).replace(/\s*\[attached:[^\]]*\]/gi, '').trim();
    if (!qText) return;
    pinnedRef.current = true; // retrying re-pins so the regenerated answer follows
    const history = buildHistoryMessages(messages.slice(0, msgIndex));
    setRating(msgIndex, null);
    updateConv(activeConvId, c => {
      const msgs = [...c.messages];
      if (!msgs[msgIndex]) return c;
      msgs[msgIndex] = { ...msgs[msgIndex], answer: '' };
      return { ...c, lastActive: Date.now(), messages: msgs };
    });
    await streamAnswer({ convId: activeConvId, qText, searchText: turn.searchQuery, att: attachment, history, msgIndex });
  };

  const search = searchQuery.trim().toLowerCase();
  const matchesSearch = (conv) => {
    if (!search) return true;
    if (conv.title.toLowerCase().includes(search)) return true;
    return conv.messages.some(m =>
      (m.question && m.question.toLowerCase().includes(search)) ||
      (m.answer && m.answer.toLowerCase().includes(search))
    );
  };
  // Every tab lists ALL saved conversations, most-recently-active first.
  const filteredConvs = conversations
    .filter(matchesSearch)
    .slice()
    .sort((a, b) => convLastActive(b) - convLastActive(a));
  const projectChats = (projId) => filteredConvs.filter(c => c.projectId === projId);
  const unsortedChats = filteredConvs.filter(c => !c.projectId);
  const visibleProjects = projects.filter(p =>
    !search || projectChats(p.id).length > 0 || p.name.toLowerCase().includes(search)
  );

  // Strip citation markers and any legacy [PRODUCTS: …] marker that may exist
  // in previously-saved conversations (the catalogue mechanism was removed).
  const cleanAnswer = (text) => text ? text.replace(/\[PRODUCTS:[^\]]*\]/gi, '').replace(/\s*\[\d+\]/g, '').trim() : '';
  const empty = messages.length === 0;
  const hasProfile = (p) => p?.systemProfile && Object.values(p.systemProfile).some(v => v);

  // Persistent per-conversation attachment indicator: shows the attached file
  // with a detach control, or — if metadata survived but the bytes didn't
  // (e.g. IndexedDB cleared) — a prompt to re-attach so follow-ups still work.
  const attachmentBar = (attachment || activeConv?.activeFile) ? (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, margin: '8px auto 0', maxWidth: 720, fontSize: 13, color: 'var(--accent-text)' }}>
      {attachment ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ background: 'var(--accent-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '4px 10px' }}>📎 {attachment.name} <span style={{ color: '#6b7280' }}>{t('file.attached')}</span></span>
            {showUploadTurns && uploadTurnsLeft != null && (
              <span style={{ color: '#6b7280', fontSize: 12 }}>
                {t('file.left', { count: uploadTurnsLeft })}
              </span>
            )}
            <button
              onClick={detachFile}
              title={t('file.detachTitle')}
              aria-label={t('file.detachLabel')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, border: '1px solid #fca5a5', background: '#fef2f2', color: '#b91c1c', cursor: 'pointer', fontSize: 12, fontWeight: 600, borderRadius: 8, padding: '4px 9px', lineHeight: 1 }}
            >
              <span aria-hidden="true" style={{ fontSize: 14 }}>×</span> {t('file.detach')}
            </button>
          </div>
          {showUploadTurns && (
            <span style={{ color: '#9ca3af', fontSize: 11, maxWidth: 460, textAlign: 'center' }}>
              {t('file.explain')}
            </span>
          )}
        </div>
      ) : (
        <>
          <span style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, padding: '4px 10px', color: '#9a3412' }}>⚠ {t('file.reattachHint', { name: activeConv.activeFile.name })}</span>
          <button onClick={() => { if (allowUpload()) fileRef.current?.click(); }} title={t('file.reattachTitle')} style={{ border: 'none', background: 'transparent', color: 'var(--accent-text)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>{t('file.reattach')}</button>
        </>
      )}
    </div>
  ) : null;

  // Persistent one-line notice below every chat input: AI disclosure (EU AI Act),
  // the upload warning, and the Privacy Policy and AI Assistant Terms.
  const inputDisclaimer = (
    <>
      {/* English in every interface language, as are its links. */}
      <p className="input-disclaimer" lang="en">
        Gia is an AI assistant and can make mistakes. Don’t share confidential info.
        {' '}
        <a className="terms-link" href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">Privacy</a>
        {' · '}
        <a className="terms-link" href={AI_TERMS_URL} target="_blank" rel="noopener noreferrer">Terms</a>
      </p>
      {uiLang !== 'en' && <p className="input-disclaimer input-governing" lang={uiLang}>{t('legal.englishGoverning')}</p>}
    </>
  );

  /*
   * Counters, straight from the server's remaining figures. Shown once the
   * member has used at least one question and until the allowance runs out
   * (the wall states it from then on). Hidden entirely when the server isn't
   * metering, so an unmetered session never shows a number we can't stand behind.
   * Upload-turns appear alongside only for tiers that actually have them.
   * showUploadTurns is derived up with the other usage values so the
   * attachment bar (built earlier) can read it without a temporal-dead-zone.
   */
  /*
   * "Have a code?" sits with the counter because that is the line about what
   * this account may do. It is also shown at the wall: someone handed a trial
   * code is most likely to be looking at it having just run out.
   */
  const codeEntry = (metered && !trial) ? (
    <span className="trial-entry">
      {codeOpen ? (
        <form className="trial-form" onSubmit={redeemCode}>
          <input
            className="trial-input"
            type="text"
            value={codeValue}
            onChange={(e) => { setCodeValue(e.target.value); if (codeState) setCodeState(null); }}
            placeholder={t('trial.placeholder')}
            aria-label={t('trial.title')}
            autoFocus
            autoComplete="off"
            spellCheck="false"
          />
          <button className="trial-submit" type="submit" disabled={codeState === 'checking' || !codeValue.trim()}>
            {codeState === 'checking' ? t('trial.checking') : t('trial.submit')}
          </button>
          <button className="trial-cancel" type="button" onClick={() => { setCodeOpen(false); setCodeState(null); }}>
            {t('common.cancel')}
          </button>
        </form>
      ) : (
        <button className="trial-have" type="button" onClick={() => { setCodeOpen(true); setCodeState(null); }}>
          {t('trial.have')}
        </button>
      )}
      {(codeState === 'invalid' || codeState === 'error') && (
        <span className="trial-message" role="status">{t(codeState === 'invalid' ? 'trial.invalid' : 'trial.error')}</span>
      )}
    </span>
  ) : null;

  // Amber below ~20% of whatever this tier's allowance is, trial or not.
  const runningLow = questionsLeft != null && questionsLeft <= lowThreshold(questionLimit);

  /*
   * HOW MUCH OF THE ALLOWANCE IS LEFT, AND HOW LOUD TO BE ABOUT IT.
   *
   * The count used to sit above the composer permanently, so every question a
   * member asked was framed by what it cost them. The plan and the numbers now
   * live in the account area, quietly, and this line only appears when the
   * number has started to matter:
   *
   *   above 25%   nothing at all
   *   25% or less "18 questions left"
   *   10% or less "7 questions left · Upgrade"
   *   nothing left  the existing wall replaces the composer, unchanged
   *
   * Ratios, not fixed counts, because 18 left means something different on a
   * 300-question plan than on a 20-question one. Nothing here changes what the
   * server allows: it is the same entitlements figures, said less often.
   */
  const NUDGE_BELOW = 0.25;
  const URGENT_BELOW = 0.10;
  const remainingShare = metered && questionLimit > 0 ? questionsLeft / questionLimit : 1;
  const nudgeUsage = metered && !blocked && !outOfQuestions && remainingShare <= NUDGE_BELOW;
  const urgentUsage = nudgeUsage && remainingShare <= URGENT_BELOW;

  const usageCounter = nudgeUsage ? (
    <p className={'usage-counter' + (urgentUsage ? ' low' : '')}>
      {t('usage.leftShort', { left: questionsLeft, count: questionsLeft })}
      {urgentUsage && (
        <>
          {' · '}
          <a
            className="usage-upgrade"
            href={PRICING_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('usage.upgrade')}
          </a>
        </>
      )}
    </p>
  ) : null;

  /*
   * Wall heading, named from the server's refusal so it says exactly which
   * limit was hit rather than a generic "limit reached". Falls back to the
   * remaining-count view when the wall is up without an explicit refusal
   * (i.e. the member has already spent the allowance before sending).
   */
  const wallHeading = (() => {
    switch (blocked?.error) {
      case 'daily_limit':
        return t('wall.daily');
      case 'feature_not_available':
        return blocked.tier_label ? t('wall.feature', { plan: blocked.tier_label }) : t('wall.featureYour');
      case 'upload_turn_limit':
        return t('wall.files', { limit: blocked.limit, plan: blocked.tier_label });
      case 'question_limit':
        return t('wall.questions', { limit: blocked.limit, plan: blocked.tier_label });
      default:
        return metered
          ? t('wall.questions', { limit: questionLimit, plan: ent.tier_label })
          : t('wall.generic');
    }
  })();

  // Soft wall: replaces the composer when the allowance is spent or the server
  // refuses a turn. Past chats and the sidebar stay fully usable — only the
  // input becomes this.
  const upgradePanel = (
    <div className="upgrade-panel" role="region" aria-label={t('wall.region')}>
      <div className="upgrade-panel-head">
        <span className="upgrade-panel-icon" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
        </span>
        <h3>{wallHeading}</h3>
      </div>
      <p>
        {/* A message written by the server is shown as the server wrote it. */}
        {blocked?.message
          ? blocked.message
          : (ent?.tier_label ? t('wall.refresh', { plan: ent.tier_label }) : t('wall.refreshNoPlan'))}
        {' '}
        {t('wall.stayFree')}
      </p>
      <a
        className="upgrade-panel-btn"
        href={PRICING_URL}
        target="_blank"
        rel="noopener noreferrer"
      >
        {t('wall.upgrade')}
      </a>
      {/* The other way out of this panel: a trial code, if someone gave you one. */}
      {codeEntry ? <div className="wall-trial">{codeEntry}</div> : null}
    </div>
  );

  // Style for the inline "+ New chat" link inside project chat lists
  const newChatInProjectStyle = {
    width: '100%',
    padding: '8px 12px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '12.5px',
    fontWeight: 500,
    color: 'var(--text-muted)',
    textAlign: 'left',
    borderRadius: '8px',
    marginBottom: '2px',
    letterSpacing: '-0.01em'
  };

  if (showLanding) {
    return (
      <I18nProvider value={i18n}>
        <div className={'App ' + (dark ? 'dark' : 'light')}>
          <LandingScreen />
        </div>
      </I18nProvider>
    );
  }

  return (
    <I18nProvider value={i18n}>
    <div className={'App ' + (dark ? 'dark' : 'light')}>
      <div className={"app-shell" + (sidebarHidden ? " sidebar-hidden" : "")}>
        <aside className={'sidebar ' + (sidebarOpen ? 'open' : '')}>
          <div className="sidebar-header">
            <div className="sidebar-brand">
              {/*
                * The mark and the wordmark start a new chat, the same action as
                * the pen beside them. They used to leave for aquanetica.com,
                * which is the one thing a person in the middle of a
                * conversation does not want from the logo; the footer link and
                * the home icon are still the way back to the site.
                *
                * A real <button> inside the <h1>, so it is reachable by keyboard
                * and announced as "New chat" rather than read out as a heading
                * that happens to be clickable.
                */}
              <h1 className="sidebar-brand-title">
                <button
                  type="button"
                  className="brand-link"
                  onClick={startNewChat}
                  title={t('toolbar.newChat')}
                  aria-label={t('toolbar.newChat')}
                >
                  <span className="brand-mark">
                    {/* Fish mark, theme-swapped like the loader; sized inside the
                        same 28px box the octopus occupied so nothing shifts. */}
                    <img className="fish-img fish-img-light" src={iconBlack} alt="" style={{ height: 14 }} />
                    <img className="fish-img fish-img-dark" src={iconWhite} alt="" style={{ height: 14 }} />
                  </span>
                  <span className="brand-wordmark">AquaNetica IQ</span>
                </button>
              </h1>
              <button className="sidebar-collapse-btn" onClick={hideSidebar} title={t('sidebar.hide')} aria-label={t('sidebar.hide')}><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="9" y1="3" x2="9" y2="21"/></svg></button>
            </div>
            <input
              className="search-input"
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder={t('sidebar.search')}
            />
            <div className="sidebar-actions">
              <button className="new-btn primary" onClick={newChat}>{t('sidebar.newChat')}</button>
              <button className="new-btn secondary" onClick={newProject}>{t('sidebar.newProject')}</button>
              
            </div>
              
          </div>
<div className="tools-section">
          <div className="tools-header">{t('sidebar.tools')}</div>
          <button
            className="tool-item"
            onClick={() => { setActiveView(v => (v === 'calc' ? 'chat' : 'calc')); setSidebarOpen(false); }}
            aria-pressed={activeView === 'calc'}
            style={activeView === 'calc' ? { background: 'var(--accent-fill)', color: '#fff' } : undefined}
          >
            {activeView === 'calc' ? t('sidebar.backToGia') : t('sidebar.calculator')}
          </button>
        </div>
          <div className="conv-list">
            {projects.length === 0 && conversations.length === 0 && (
              <div className="conv-empty">{t('sidebar.empty')}</div>
            )}

            {visibleProjects.map(p => {
              const chats = projectChats(p.id);
              const isExpanded = expandedProjects.has(p.id) || !!search;
              const isActive = activeProjectId === p.id && !activeConvId;
              return (
                <div key={p.id} className="project-group">
                  <div
                    className={'project-header ' + (isActive ? 'active' : '')}
                    onClick={() => toggleProject(p.id)}
                  >
                    <span className={'project-chevron ' + (isExpanded ? 'open' : '')}>›</span>
                    <span
                      className="project-name"
                      onClick={(e) => { e.stopPropagation(); selectProject(p.id); }}
                    >
                      {p.name}
                    </span>
                    {hasProfile(p) && <span className="project-profile-dot" title={t('sidebar.profileSet')} />}
                    <div className="project-actions">
                      <button className="proj-action" onClick={(e) => { e.stopPropagation(); newChatInProject(p.id); }} title={t('sidebar.newInProject')} aria-label={t('sidebar.newInProject')}>+</button>
                      <button className="proj-action" onClick={(e) => { e.stopPropagation(); setEditingProjectId(p.id); }} title={t('sidebar.editProfile')} aria-label={t('sidebar.editProfile')}>⚙</button>
                      <button className="proj-action" onClick={(e) => { e.stopPropagation(); renameProject(p.id); }} title={t('sidebar.rename')} aria-label={t('sidebar.rename')}>✎</button>
                      <button className="proj-action" onClick={(e) => { e.stopPropagation(); deleteProject(p.id); }} title={t('sidebar.delete')} aria-label={t('sidebar.delete')}>×</button>
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="project-chats">
                      <button
                        style={newChatInProjectStyle}
                        onClick={() => newChatInProject(p.id)}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--hover)'; e.currentTarget.style.color = 'var(--text)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-muted)'; }}
                      >
                        {t('sidebar.newChat')}
                      </button>
                      {chats.length === 0 && (
                        <div className="project-empty">{t('sidebar.projectEmpty')}</div>
                      )}
                      {chats.map(c => (
                        <div
                          key={c.id}
                          className={'conv-item ' + (c.id === activeConvId ? 'active' : '')}
                          onClick={() => selectChat(c.id)}
                        >
                          <span className="conv-title">{c.title}</span>
                          <button className="conv-delete" onClick={(e) => deleteChat(c.id, e)}>×</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {unsortedChats.length > 0 && (
              <>
                {projects.length > 0 && <div className="conv-section-label">{t('sidebar.otherChats')}</div>}
                {unsortedChats.map(c => (
                  <div
                    key={c.id}
                    className={'conv-item ' + (c.id === activeConvId ? 'active' : '')}
                    onClick={() => selectChat(c.id)}
                  >
                    <span className="conv-title">{c.title}</span>
                    <button className="conv-delete" onClick={(e) => deleteChat(c.id, e)}>×</button>
                  </div>
                ))}
              </>
            )}

            {search && filteredConvs.length === 0 && visibleProjects.length === 0 && (
              <div className="conv-empty">{t('sidebar.noMatches', { q: searchQuery })}</div>
            )}
          </div>
          {showAccount && (
            <div className="sidebar-account">
              <div className="sidebar-account-row">
                <div className="sidebar-account-id">
                  <span className="sidebar-account-name">{auth.user.name || auth.user.email || t('sidebar.signedIn')}</span>
                  {(planLabel || CUSTOMER_TIER_LABELS[auth.user.tier]) && (
                    <span className="sidebar-account-tier">{planLabel || CUSTOMER_TIER_LABELS[auth.user.tier]}</span>
                  )}
                </div>
                <button
                  className="sidebar-logout"
                  onClick={() => setSignOutOpen(true)}
                  disabled={signingOut}
                  title={t('sidebar.signOut')}
                >
                  {signingOut ? t('sidebar.signingOut') : t('sidebar.signOut')}
                </button>
              </div>
              {/*
                * Where the plan and the numbers live now. Read straight off the
                * entitlements the app already holds, nothing cached, nothing
                * new in storage. Secondary type, under the account row, so it
                * is there when someone looks for it and invisible when they
                * are not. Manage plan goes to the same pricing page every
                * other upgrade link in the app already uses.
                */}
              {metered && (
                <div className="sidebar-plan">
                  <span className="sidebar-plan-usage">
                    {t('usage.remaining', { left: questionsLeft, limit: questionLimit, count: questionsLeft })}
                  </span>
                  {trial && trialEnds && (
                    <span className="sidebar-plan-trial">
                      {/* Redeeming a code is confirmed here, where the trial
                          now lives, rather than left to a silent change. */}
                      {codeState === 'done'
                        ? t('trial.success', { date: trialEnds })
                        : t('trial.ends', { date: trialEnds })}
                    </span>
                  )}
                  <a
                    className="sidebar-plan-manage"
                    href={PRICING_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('sidebar.managePlan')}
                  </a>
                  {codeEntry}
                </div>
              )}
            </div>
          )}
          <div className="sidebar-footer">
            <a
              className="sidebar-back-link"
              href="https://aquanetica.com/feed/"
              target="_blank"
              rel="noopener noreferrer"
              title={t('sidebar.communityTitle')}
            >
              {t('sidebar.community')}
            </a>
            <button className="sidebar-help-btn" onClick={() => setShowHelp(true)} aria-label={t('sidebar.help')} title={t('sidebar.help')}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            </button>
          </div>
          {/* Operator company details, tucked under the sidebar footer on the chat screens. */}
          <CompanyFooter className="sidebar-company" />
        </aside>

        <div
          className={'main-area' + (activeView === 'chat' ? ' is-chat' : '') + (activeView === 'chat' && messages.length > 0 ? ' has-composer' : '')}
          onDragEnter={onAreaDragEnter}
          onDragOver={onAreaDragOver}
          onDragLeave={onAreaDragLeave}
          onDrop={onAreaDrop}
        >
          {dragActive && activeView === 'chat' && (
            <div className="drop-overlay" aria-hidden="true">
              <div className="drop-overlay-label">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                {t('drop.label')}
              </div>
            </div>
          )}
          <header className="topbar" ref={topbarRef}>
            <button className="topbar-show-sidebar" onClick={() => setSidebarHidden(false)} title={t('toolbar.showSidebar')} aria-label={t('toolbar.showSidebar')}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="9" y1="3" x2="9" y2="21"/></svg></button>
            <button className="icon-btn menu-btn" onClick={() => setSidebarOpen(!sidebarOpen)} aria-label={t('toolbar.toggleSidebar')}>☰</button>
            <button className="topbar-newchat" onClick={startNewChat} aria-label={t('toolbar.newChat')} title={t('toolbar.newChat')}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
              <span className="topbar-newchat-label">{t('toolbar.newChat')}</span>
            </button>
            {activeProject && (
              <div className="topbar-context">
                <span className="topbar-context-label">{t('toolbar.inProject')}</span> <strong>{activeProject.name}</strong>
                {hasProfile(activeProject) && <span className="topbar-profile-dot" title={t('sidebar.profileSet')} />}
              </div>
            )}
            {/* Always-visible way back to the community hub — on phones the
                sidebar starts closed, so its footer link alone isn't enough.
                New tab so an in-progress conversation is never lost. */}
            <a
              className="icon-btn topbar-home"
              href="https://aquanetica.com/feed/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t('toolbar.home')}
              title={t('toolbar.homeTitle')}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
            </a>
            {/*
              The calculator entry lives in the toolbar on the chat view. As a
              floating button in the bottom-right corner it sat on the send
              button and the disclaimer at phone widths, and anywhere in that
              corner it would cover the last lines of an answer. Same label and
              title as before, so anything that finds it by name still does.
            */}
            {activeView === 'chat' && (
              <button
                type="button"
                ref={langButtonRef}
                className="icon-btn topbar-lang"
                onClick={() => setShowLangSheet(true)}
                aria-haspopup="dialog"
                aria-label={t('toolbar.answerLanguage', { name: answerLang === 'auto' ? t('lang.auto') : languageInfo(answerLang).native })}
                title={t('toolbar.answerLanguageTitle')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M3 12h18"/><path d="M12 3a14 14 0 0 1 0 18"/><path d="M12 3a14 14 0 0 0 0 18"/></svg>
                <span className="topbar-lang-code">{languageInfo(answerLang).short}</span>
              </button>
            )}
            {activeView === 'chat' && (
              <button
                type="button"
                className="icon-btn topbar-calc"
                onClick={() => { setActiveView('calc'); setSidebarOpen(false); }}
                title={t('toolbar.calc')}
                aria-label={t('toolbar.calc')}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2"/><line x1="8" y1="6" x2="16" y2="6"/><line x1="8" y1="11" x2="8" y2="11.01"/><line x1="12" y1="11" x2="12" y2="11.01"/><line x1="16" y1="11" x2="16" y2="11.01"/><line x1="8" y1="15" x2="8" y2="15.01"/><line x1="12" y1="15" x2="12" y2="15.01"/><line x1="16" y1="15" x2="16" y2="15.01"/></svg>
              </button>
            )}
            <button className="icon-btn" onClick={() => setDark(!dark)} aria-label={t('toolbar.theme')} title={t('toolbar.theme')}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
            </button>
          </header>

          <main className="content" ref={contentRef} style={activeView === 'calc' ? { maxWidth: 'none', width: '100%', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 } : undefined}>
            {activeView === "chat" ? (
    <>
      {empty && (
              <div className="hero">
                <div className="hero-icon">
                  {/* Fish mark, theme-swapped like the loader; sized to the
                      octopus's visual weight inside the same 48px tinted box. */}
                  <img className="fish-img fish-img-light" src={iconBlack} alt="" style={{ height: 24 }} />
                  <img className="fish-img fish-img-dark" src={iconWhite} alt="" style={{ height: 24 }} />
                </div>
                <h2 className="hero-title">
                  {activeProject ? activeProject.name : t('hero.title')}
                </h2>
                <p className="hero-subtitle">
                  {activeProject
                    ? (hasProfile(activeProject)
                        ? t('hero.calibrated')
                        : t('hero.noProfile'))
                    : t('hero.subtitle')}
                </p>

                {usageCounter}
                {atWall ? upgradePanel : (
                <div className="search-wrap composer-bar">
                  <button type="button" className="attach-btn" onClick={() => { if (allowUpload()) fileRef.current?.click(); }} title={t('composer.attachTitle')} aria-label={t('composer.attachLabel')} data-active={attachment ? '1' : '0'}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  </button>
                  <textarea
                    ref={inputRef}
                    rows={1}
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    onKeyDown={onComposerKeyDown}
                    onPaste={onComposerPaste}
                    enterKeyHint={composerEnterHint}
                    placeholder={starter.placeholderHero}
                    lang={starterLang}
                    autoFocus
                  />
                  <button className="send" onClick={() => ask(query)} disabled={(!query.trim() && !attachment) || loading} aria-label={t('composer.send')}>↑</button>
                </div>
                )}
                {!atWall && attachmentBar}
                {inputDisclaimer}

                {!activeProject && !atWall && (
                  <div className="examples">
                    {examples.map((ex, i) => (
                      <button key={i} className="chip" lang={starterLang} onClick={() => ask(ex, { searchQuery: englishExamples[i] })}>{ex}</button>
                    ))}
                  </div>
                )}

                {activeProject && (() => {
                  let saved = [];
                  try {
                    const all = JSON.parse(localStorage.getItem('aqua-iq-calc-results') || '{}');
                    saved = Array.isArray(all[activeProject.id]) ? all[activeProject.id] : [];
                  } catch (e) { saved = []; }
                  const staleCount = saved.filter(isEntryStale).length;
                  return (
                    <div className="saved-calcs" data-tick={calcTick} style={{ width: '100%', maxWidth: 720, margin: '24px auto 0', textAlign: 'left' }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 10 }}>
                        {t('saved.title')}{saved.length ? ` (${saved.length})` : ''}
                      </div>
                      {staleCount > 0 && (
                        <div style={{ background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 10, padding: '10px 12px', marginBottom: 10, fontSize: 12.5, lineHeight: 1.5, color: '#9a3412' }}>
                          <strong>{t('saved.staleSummary', { count: staleCount })}</strong>{' '}
                          {t('saved.staleAction', { count: staleCount })}
                        </div>
                      )}
                      {saved.length === 0 ? (
                        <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>
                          {t('saved.none')}
                        </p>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          {saved.map((r) => {
                            const stale = isEntryStale(r);
                            const editable = !!r.rawInputs;
                            // Same vocabulary as the method-version case: a result
                            // is "superseded" whether the calculator was corrected
                            // or the operator revised the inputs.
                            const revises = revisionSource(r);
                            const revisedBy = findRevisionOf(r, saved);
                            return (
                            <div key={r.id} style={{ border: '1px solid ' + (stale ? '#fdba74' : 'var(--border)'), borderRadius: 12, padding: '16px 18px', background: stale ? '#fffbf5' : '#ffffff', boxShadow: '0 1px 2px rgba(16,40,37,0.04)' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline', marginBottom: 11 }}>
                                <span style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                                  <strong style={{ fontSize: 15, fontWeight: 800, color: 'var(--accent-text)', letterSpacing: '-0.01em' }}>{tc(r.calcLabel)}</strong>
                                  {revises && (
                                    <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--accent-text)', background: 'var(--accent-bg)', border: '1px solid var(--border)', borderRadius: 6, padding: '2px 6px' }}>
                                      {t('saved.revision')}
                                    </span>
                                  )}
                                  {revisedBy && (
                                    <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: '#6e6e73', background: '#f1f2f4', border: '1px solid #e0e2e6', borderRadius: 6, padding: '2px 6px' }}>
                                      {t('saved.superseded')}
                                    </span>
                                  )}
                                </span>
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, whiteSpace: 'nowrap' }}>
                                  <span style={{ fontSize: 11, color: '#9ca3af' }}>{r.ts}</span>
                                  <button onClick={() => { try { const all = JSON.parse(localStorage.getItem('aqua-iq-calc-results') || '{}'); const arr = Array.isArray(all[activeProject.id]) ? all[activeProject.id] : []; all[activeProject.id] = arr.filter((x) => x.id !== r.id); localStorage.setItem('aqua-iq-calc-results', JSON.stringify(all)); setCalcTick((x) => x + 1); } catch (e) {} }} title={t('saved.delete')} aria-label={t('saved.delete')} style={{ border: 'none', background: 'transparent', color: '#b91c1c', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 0 }}>×</button>
                                </div>
                              </div>

                              {stale && (
                                <div style={{ background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 10, padding: '10px 12px', marginBottom: 12, fontSize: 11.5, lineHeight: 1.55, color: '#9a3412' }}>
                                  <strong>{isMethodStale(r) ? t('saved.methodChanged') : t('saved.misreadNumber')}</strong> {staleNotes(r).map(tc).join(' ')}{misreadValues(r) ? ` ${t('calc.misreadNote', { values: misreadValues(r) })}` : ''} {t('saved.rerunBefore')}
                                </div>
                              )}

                              {revises && (
                                <div style={{ fontSize: 11.5, color: 'var(--accent-text)', marginBottom: 10, lineHeight: 1.5 }}>
                                  {t('saved.revises', { name: revises.calcLabel ? tc(revises.calcLabel) : t('saved.result'), ts: revises.ts })}
                                </div>
                              )}
                              {revisedBy && (
                                <div style={{ fontSize: 11.5, color: '#6e6e73', marginBottom: 10, lineHeight: 1.5 }}>
                                  {t('saved.supersededBy', { ts: revisedBy.ts })}
                                </div>
                              )}

                              {/* Input summary, as the worksheet panel shows it. */}
                              {(r.inputs || []).length > 0 && (
                                <div style={{ fontSize: 12, color: '#6e6e73', marginBottom: 13, lineHeight: 1.65 }}>
                                  {r.inputs.map((i, j) => (
                                    <span key={j}>
                                      {tc(i.label)}: <strong>{num(tc(i.value))}</strong>{i.unit ? ` ${i.unit}` : ''}
                                      {j < r.inputs.length - 1 ? '  ·  ' : ''}
                                    </span>
                                  ))}
                                </div>
                              )}

                              {/* One row per output rather than a single wrapped line. */}
                              {(r.outputs || []).map((o, j) => {
                                const prose = isProseOutput(o);
                                const rowStyle = {
                                  padding: '8px 12px', borderRadius: 9, marginBottom: 6,
                                  background: o.flag === 'over' ? '#fdeceb' : o.flag === 'key' ? 'var(--accent-bg)' : '#f3f6f5',
                                  fontWeight: o.flag === 'key' ? 700 : 500,
                                  fontSize: 13, lineHeight: 1.5,
                                  color: stale ? '#9ca3af' : '#0d1f1d',
                                };
                                if (prose) {
                                  return (
                                    <div key={j} style={rowStyle}>
                                      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em', color: '#6e6e73', marginBottom: 4, fontWeight: 700 }}>
                                        {tc(o.label)}
                                      </div>
                                      <div style={{ fontWeight: 400 }}>{num(tc(o.value))}</div>
                                    </div>
                                  );
                                }
                                return (
                                  <div key={j} style={{ ...rowStyle, display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                                    <span>{tc(o.label)}{o.flag === 'over' ? '  ' + t('saved.check') : ''}</span>
                                    <span style={{ fontVariantNumeric: 'tabular-nums', textAlign: 'right', whiteSpace: 'nowrap', color: o.flag === 'over' && !stale ? '#b3261e' : undefined }}>
                                      {num(tc(o.value))}{o.unit ? ` ${o.unit}` : ''}
                                    </span>
                                  </div>
                                );
                              })}

                              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                                <button
                                  onClick={() => editSavedCalc(r)}
                                  disabled={!editable}
                                  title={editable
                                    ? t('saved.editTitle')
                                    : t('saved.editDisabledTitle')}
                                  style={{
                                    padding: '9px 14px', minHeight: 44,
                                    background: '#fff', color: editable ? 'var(--accent-text)' : '#9ca3af',
                                    border: '1px solid ' + (editable ? 'var(--accent)' : '#e5e7eb'),
                                    borderRadius: 10, fontSize: 13, fontWeight: 700,
                                    cursor: editable ? 'pointer' : 'not-allowed',
                                    fontFamily: 'inherit',
                                  }}
                                >
                                  {t('saved.edit')}
                                </button>
                                {!editable && (
                                  <span style={{ fontSize: 11.5, color: '#9ca3af', alignSelf: 'center', lineHeight: 1.45 }}>
                                    {t('saved.predates')}
                                  </span>
                                )}
                              </div>
                            </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })()}

              </div>
            )}

            {messages.map((turn, i) => {
              const streaming = loading && i === busyIndex;
              const rating = ratings[activeConvId]?.[i];
              return (
                <article key={i} className="turn">
                  <h2 className="question">{turn.question}</h2>
                  {turn.answer ? (
                    <>
                      <div
                        className="answer"
                        ref={(el) => { answerRefs.current[i] = el; }}
                      >
                        {streaming ? (
                          <StreamingMarkdown text={cleanAnswer(turn.answer)} />
                        ) : (
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {cleanAnswer(turn.answer)}
                          </ReactMarkdown>
                        )}
                        {streaming && (
                          <FishLoader phase="writing" size={18} inline />
                        )}
                      </div>
                      {!streaming && (
                        <div className="answer-actions">
                          <button
                            className="answer-action"
                            onClick={() => copyAnswer(i, cleanAnswer(turn.answer))}
                            title={t('answer.copyTitle')}
                            aria-label={t('answer.copyTitle')}
                          >
                            {copiedIndex === i ? (
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                            ) : (
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="12" height="12" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                            )}
                            <span>{copiedIndex === i ? t('answer.copied') : t('answer.copy')}</span>
                          </button>
                          <button
                            className="answer-action"
                            onClick={() => retryTurn(i)}
                            disabled={loading}
                            title={t('answer.retryTitle')}
                            aria-label={t('answer.retryLabel')}
                          >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
                            <span>{t('answer.retry')}</span>
                          </button>
                          <button
                            className={'answer-action icon-only' + (rating === 'up' ? ' rated' : '')}
                            onClick={() => setRating(i, 'up')}
                            title={t('answer.good')}
                            aria-label={t('answer.good')}
                            aria-pressed={rating === 'up'}
                          >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill={rating === 'up' ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>
                          </button>
                          <button
                            className={'answer-action icon-only' + (rating === 'down' ? ' rated' : '')}
                            onClick={() => setRating(i, 'down')}
                            title={t('answer.bad')}
                            aria-label={t('answer.bad')}
                            aria-pressed={rating === 'down'}
                          >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill={rating === 'down' ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"/></svg>
                          </button>
                        </div>
                      )}
                      {!streaming && turn.sourcing && (
                        <button
                          type="button"
                          className="sourcing-link"
                          onClick={() => setProcurement({
                            item: turn.sourcing.item,
                            specs: turn.sourcing.specs,
                            models: turn.sourcing.models || [],
                            // The question that produced this answer. It is the
                            // only field that says which questions turn into
                            // buying intent, so it travels with the enquiry.
                            question: turn.question || '',
                          })}
                        >
                          {t('answer.sourcing')}
                        </button>
                      )}
                      {i === 0 && (
                      <div className="disclaimer" lang="en">
                        {/* Legal note: English in every interface language. */}
                        <span className="disclaimer-label">Important:</span>
                        This is general guidance only. Always verify with a qualified aquaculture professional before acting. AquaNetica Group Ltd accepts no responsibility for outcomes resulting from use of this information.
                      </div>
                      )}
                    </>
                  ) : (
                    <div className="loading-row">
                      <FishLoader phase={loadPhase === 'found' ? 'found' : 'searching'} size={24} />
                      <span className="fish-label">{t('answer.searching')}</span>
                    </div>
                  )}
                </article>
              );
            })}

            <div ref={endRef} />
    </>
  ) : (
    <div className="calc-host">
      <CalculatorDashboard
        pendingEdit={pendingEdit}
        onEditLoaded={() => setPendingEdit(null)}
      />
    </div>
  )}
  {activeView === 'calc' && (
    <div className="calc-page-legal">
      {/* The company line sits in the sidebar; the Calculator has no notice line, so its links go here. */}
      <p className="company-footer" lang="en">
        <a className="terms-link" href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">Privacy</a>
        {' · '}
        <a className="terms-link" href={TERMS_URL} target="_blank" rel="noopener noreferrer">Terms</a>
      </p>
    </div>
  )}
</main>

          {activeView === 'chat' && messages.length > 0 && showJump && (
            <button type="button" className="scroll-latest" onClick={jumpToLatest}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><polyline points="19 12 12 19 5 12"/></svg>
              {t('answer.scrollLatest')}
            </button>
          )}

          {activeView === 'chat' && messages.length > 0 && (
            <div className="footer-input" ref={footerRef}>
              {usageCounter}
              {atWall ? upgradePanel : (
              <div className="search-wrap composer-bar">
                <button type="button" className="attach-btn" onClick={() => { if (allowUpload()) fileRef.current?.click(); }} title={t('composer.attachTitle')} aria-label={t('composer.attachLabel')} data-active={attachment ? '1' : '0'}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                </button>
                <textarea
                  ref={inputRef}
                  rows={1}
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  onKeyDown={onComposerKeyDown}
                  onPaste={onComposerPaste}
                  enterKeyHint={composerEnterHint}
                  placeholder={starter.placeholderFollowUp}
                  lang={starterLang}
                />
                <button className="send" onClick={() => ask(query)} disabled={(!query.trim() && !attachment) || loading} aria-label={t('composer.send')}>↑</button>
              </div>
              )}
              {!atWall && attachmentBar}
              {inputDisclaimer}
            </div>
          )}
        </div>

        {sidebarOpen && (
          <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />
        )}
        <input ref={fileRef} type="file" accept={UPLOAD_ACCEPT} onChange={onPickFile} style={{ display: 'none' }} />
        {activeView === 'calc' && (
          <button
            onClick={() => setActiveView('chat')}
            title={t('calc.minimiseTitle')}
            aria-label={t('calc.minimiseLabel')}
            style={{ position: 'fixed', right: 20, bottom: 20, zIndex: 60, width: 48, height: 48, borderRadius: 24, border: 'none', cursor: 'pointer', background: 'var(--accent-fill)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 6px 20px rgba(81,145,147,0.45)' }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/><line x1="5" y1="19" x2="19" y2="19"/></svg>
          </button>
        )}
        {editingProject && (
          <ProjectProfileModal
            project={editingProject}
            onSave={(profile) => saveProfile(editingProject.id, profile)}
            onClose={() => setEditingProjectId(null)}
          />
        )}
        {showLangSheet && (
          <LanguageSheet current={answerLang} onChoose={chooseAnswerLang} onClose={closeLangSheet} />
        )}
        {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}
        {showTerms && <TermsModal onClose={() => setShowTerms(false)} />}
        {procurement && (
          <ProcurementModal
            initial={procurement}
            user={auth.user}
            conversationId={activeConvId}
            onClose={() => setProcurement(null)}
          />
        )}
        {signOutOpen && (
          <SignOutModal
            busy={signingOut}
            onCancel={() => setSignOutOpen(false)}
            onConfirm={logout}
          />
        )}

        {featurePrompt && (
          <FeaturePrompt {...featurePrompt} onClose={() => setFeaturePrompt(null)} />
        )}
      </div>
    </div>
    </I18nProvider>
  );
}

export default App;
